import React, { Component } from 'react';
import './RecipeBook.css';
import './RecipeBook-modal.css';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import NativeSelect from '@mui/material/NativeSelect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faTrash, faEdit, faShareAlt, faBook, faArrowLeft,
  faUtensils, faExternalLinkAlt, faCopy, faPaste, faGlobe,
  faClock, faFire, faSearch, faTimes, faLink
} from '@fortawesome/free-solid-svg-icons';
import {
  createRecipeBook, deleteRecipeBook, getRecipeBooks,
  shareRecipeBook, unshareRecipeBook, getRecipeBookShareUsers,
  updateRecipeBook, addRecipe, deleteRecipe, updateRecipe, getRecipesByBook,
  getRecipesFromAllBooks, getRecipesFromAllBooksList,
  getRecipesByCategory, getRecipeDetail,
  importRecipeByUrl, importRecipeByText, uploadRecipeImage, importRecipeHtml,
  searchRecipes,
  getCategories,
  updateSelectedRecipeBook,
  generateShareToken,
  revokeShareToken
} from '../../api/backend';
import { HOST } from '../../constants/api';
import { Application } from '../../core/app';
import { escapeHtml } from '../../utils/sanitize';
import { showToast } from '../../toast';

const DIFFICULTIES = [
  { value: 'easy', label: 'Easy' },
  { value: 'medium', label: 'Medium' },
  { value: 'hard', label: 'Hard' },
  { value: 'challenging', label: 'Challenging' },
];

class RecipeBook extends Component {
  state = {
    books: [],
    shareUsers: [],
    categories: [],
    selectedBookId: '',
    selectedBook: null,
    newBookName: '',
    newBookDesc: '',
    editBookName: '',
    editBookDesc: '',
    editBookAllowSharedEdit: false,
    selectedShareUserId: '',
    showDeleteModal: false,
    bookToDelete: null,
    showShareModal: false,
    showShareLinkModal: false,
    shareLinkUrl: '',
    shareLinkLoading: false,
    showAddRecipeModal: false,
    showImportUrlModal: false,
    importedPreview: null,
    showImportTextModal: false,
    showImportHtmlModal: false,
    showEditRecipeModal: false,
    showRecipeDetail: false,
    viewingRecipe: null,
    showEditBookForm: false,
    recipes: [],
    isLoading: true,
    // Serving scale (for viewing recipes)
    customServings: null,
    // Category picker
    showCategoryPicker: false,
    // Filtering
    selectedCategoryId: '',
    searchQuery: '',
    currentPage: 1,
    recipeTotal: 0,
    // Add recipe form
    recipeTitle: '',
    recipeDirection: '',
    recipeServings: '',
    recipePrepTime: '',
    recipeCookTime: '',
    recipeDifficulty: 'easy',
    recipeNotes: '',
    recipeIngredients: [{ name: '', quantity: '', unit: '', notes: '' }],
    recipeCategories: [],
    // Edit recipe
    editingRecipeId: null,
    // Import
    importUrl: '',
    importText: '',
    htmlFile: null,
    importHtmlLoading: false,
    // New UI: view navigation
    currentView: 'home', // 'home' | 'category' | 'search'
    showSearchBar: false,
    showAddMenu: false,
    categoryRecipes: {}, // cache of recipes per category for rotating images
    categoryImageIndex: {}, // current image index per category for carousel
    // Serving adjustment tracking
    effectiveServings: null, // current target servings (scaled)
    showMetric: false, // toggle metric (grams/ml) display
    metricOverrides: {}, // { [ingredientIndex]: 'grams' | 'ml' } — per-ingredient override
    metricContextMenu: null, // { idx, x, y } — position of context menu
    // Interactive recipe tracking
    checkedIngredients: new Set(),
    activeDirectionStep: -1,
    heroTextColor: '#fff',
    // All Books list view (progressive loading)
    allBooksListRecipes: [], // lightweight recipe list for "All Books"
    allBooksListPage: 1,
    allBooksListLoading: false,
    allBooksListHasMore: true,
    allBooksListTotal: 0,
  };

  // Read state from URL path
  // Paths: /recipes/1, /recipes/1/category/5, /recipes/1/search/pasta, /recipes/1/recipe/42
  getUrlState = () => {
    const path = window.location.pathname;
    const parts = path.split('/').filter(Boolean); // ['recipes', '1', 'category', '5']
    
    const result = {
      bookId: '',
      view: 'home',
      categoryId: '',
      searchQuery: '',
      recipeId: '',
    };

    if (parts[0] !== 'recipes') return result;

    // Book ID is always the second segment
    if (parts[1]) result.bookId = parts[1];

    if (parts[2] === 'category' && parts[3]) {
      result.view = 'category';
      result.categoryId = parts[3];
      // Check for recipe detail: /recipes/1/category/5/recipe/42
      if (parts[4] === 'recipe' && parts[5]) {
        result.recipeId = parts[5];
      }
    } else if (parts[2] === 'search' && parts[3]) {
      result.view = 'search';
      result.searchQuery = decodeURIComponent(parts[3]);
      // Check for recipe detail: /recipes/1/search/pasta/recipe/42
      if (parts[4] === 'recipe' && parts[5]) {
        result.recipeId = parts[5];
      }
    } else if (parts[2] === 'recipe' && parts[3]) {
      // /recipes/1/recipe/42
      result.recipeId = parts[3];
    }

    return result;
  }

  // Update URL to path-based URL
  setUrlState = (bookId, view, categoryId, searchQuery, recipeId) => {
    let path = '/recipes';
    if (bookId) path += `/${bookId}`;
    if (view === 'category' && categoryId) path += `/category/${categoryId}`;
    else if (view === 'search' && searchQuery) path += `/search/${encodeURIComponent(searchQuery)}`;
    if (recipeId) path += `/recipe/${recipeId}`;
    window.history.pushState(null, '', path);
  }

  _attachScrollListener = () => {
    if (this._contentEl && !this._scrollListenerAttached) {
      this._contentEl.addEventListener('scroll', this._scrollHandler);
      this._scrollListenerAttached = true;
    }
  };

  componentDidMount() {
    const urlState = this.getUrlState();
    this.loadData(urlState);

    // Handle back/forward button — reload with new URL state
    this._popstateHandler = () => {
      const newState = this.getUrlState();
      this.loadData(newState);
    };
    window.addEventListener('popstate', this._popstateHandler);

    // Infinite scroll handler for "All Recipes" list view
    this._scrollHandler = () => {
      if (this.state.currentView !== 'category' || this.state.selectedCategoryId !== '__all__') return;
      if (this.state.allBooksListLoading || !this.state.allBooksListHasMore) return;

      const el = this._contentEl;
      if (!el) return;
      // Check if near bottom of the scrollable content area
      const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distFromBottom < 500) {
        this.loadMoreAllBooks();
      }
    };

    // Register methods with App-level refs so Header can call them
    if (this.props.refCallback) {
      this.props.refCallback({
        selectBook: this.selectBook,
        setShowAddMenu: (v) => this.setState({ showAddMenu: v }),
        setShowSearchBar: (v) => this.setState({ showSearchBar: v }),
        closeRecipeDetail: this.closeRecipeDetail,
        openEditRecipe: this.openEditRecipe,
        deleteRecipe: (recipe) => {
          this.setState({ showDeleteModal: true, bookToDelete: null, recipeToDelete: recipe });
        },
        shareRecipe: () => this.setState({ showShareModal: true }),
        shareRecipeLink: () => this.openShareLink(this.state.viewingRecipe),
        saveEditRecipe: this.saveEditRecipe,
        cancelEditRecipe: this.cancelEditRecipe,
      });
    }

    // Sync initial state with App-level context
    if (this.props.onStateChange) {
      const selectedBook = this.state.books.find((b) => String(b.id) === String(this.state.selectedBookId)) || null;
      const recipeBook = this.state.viewingRecipe ? this.state.books.find((b) => String(b.id) === String(this.state.viewingRecipe.recipe_book_id)) : null;
      this.props.onStateChange({
        books: this.state.books,
        selectedBookId: this.state.selectedBookId,
        selectedBook,
        isLoading: this.state.isLoading,
        currentView: this.state.currentView,
        viewingRecipe: this.state.viewingRecipe,
        isOwner: recipeBook ? recipeBook.is_owner : (selectedBook ? selectedBook.is_owner : false),
        canEditRecipe: (recipeBook ? (recipeBook.is_owner || recipeBook.allow_shared_edit) : (selectedBook ? (selectedBook.is_owner || selectedBook.allow_shared_edit) : false)),
        showAddRecipeModal: this.state.showAddRecipeModal,
        recipeBookId: this.state.viewingRecipe ? this.state.viewingRecipe.recipe_book_id : null,
      });
    }

    // Dismiss context menu on click/pointer outside
    this._clickHandler = (e) => {
      if (this._skipClickEvents > 0) {
        this._skipClickEvents = Math.max(0, this._skipClickEvents - 1);
        return;
      }
      if (this.state.metricContextMenu) {
        // Check if click is inside the menu using ref
        if (this._metricMenuEl && this._metricMenuEl.contains(e.target)) {
          return; // click is inside the menu, don't close
        }
        this.hideMetricContextMenu();
      }
    };
    document.addEventListener('click', this._clickHandler);
    document.addEventListener('pointerdown', this._clickHandler);
    document.addEventListener('pointerup', this._clickHandler);

    // Rotate category images every 5 seconds - only cycle through recipes with images
    this._imageInterval = setInterval(() => {
      this.setState(prev => {
        const newIndex = { ...prev.categoryImageIndex };
        // Cycle "All Recipes" image using categoryRecipes cache (same source as render)
        const allRecipes = prev.categoryRecipes['__all__'] || [];
        const allWithImages = allRecipes.filter(r => r.image_url && r.image_url.trim() !== '');
        if (allWithImages.length > 0) {
          const idx = newIndex['__all__'] ?? 0;
          newIndex['__all__'] = (idx + 1) % allWithImages.length;
        }
        for (const catId in prev.categoryRecipes) {
          const recipes = prev.categoryRecipes[catId];
          if (recipes && recipes.length > 0) {
            // Only cycle through recipes that have images
            const withImages = recipes.filter(r => r.image_url);
            if (withImages.length > 0) {
              const idx = newIndex[catId] ?? 0;
              newIndex[catId] = (idx + 1) % withImages.length;
            }
          }
        }
        return { categoryImageIndex: newIndex };
      });
    }, 5000);
  }

  componentWillUnmount() {
    clearInterval(this._imageInterval);
    clearInterval(this._allBooksInterval);
    window.removeEventListener('popstate', this._popstateHandler);
    if (this._contentEl) this._contentEl.removeEventListener('scroll', this._scrollHandler);
    clearInterval(this._allBooksInterval);
    if (this._clickHandler) {
      document.removeEventListener('click', this._clickHandler);
      document.removeEventListener('pointerdown', this._clickHandler);
      document.removeEventListener('pointerup', this._clickHandler);
    }
  }

  loadCategories = (bookId = null) => {
    this.loadCategoriesPromise(bookId);
  }

  loadRecipes = (bookId, categoryId = null) => {
    this.loadRecipesPromise(bookId, categoryId);
  }

  // Derive category recipes from the already-loaded recipes array (no extra API calls)
  deriveCategoryRecipes = (recipes, categories) => {
    const result = {};
    recipes.forEach(recipe => {
      if (recipe.categories && recipe.categories.length > 0) {
        recipe.categories.forEach(cat => {
          if (!result[cat.id]) result[cat.id] = [];
          result[cat.id].push(recipe);
        });
      }
    });
    return result;
  }

  // Persist key state to URL
  saveState = (recipeId) => {
    this.setUrlState(
      this.state.selectedBookId,
      this.state.currentView,
      this.state.selectedCategoryId,
      this.state.searchQuery,
      recipeId
    );
  }

  loadData = (urlState = null) => {
    // If books are already loaded, skip API calls and just update state from URL
    if (this.state.books && this.state.books.length > 0) {
      this._loadDataFromCache(urlState, this.state.books, this.state.shareUsers);
      return;
    }

    // Prevent duplicate loading calls (synchronous guard)
    if (this._loadingData) return;
    this._loadingData = true;

    this.setState({ isLoading: true });
    Promise.allSettled([getRecipeBooks(), getRecipeBookShareUsers()]).then(([booksResult, usersResult]) => {
      this._loadingData = false;
      const books = (booksResult.status === 'fulfilled' && booksResult.value?.data) || [];
      const shareUsers = (usersResult.status === 'fulfilled' && usersResult.value?.data) || [];
      
      if (booksResult.status === 'rejected') {
        console.error('Failed to load recipe books:', booksResult.reason);
      }
      if (usersResult.status === 'rejected') {
        console.error('Failed to load share users:', usersResult.reason);
      }
      
      this._loadDataFromCache(urlState, books, shareUsers);
    });
  };

  // Update state from URL using provided books/shareUsers
  _loadDataFromCache = (urlState = null, books, shareUsers) => {
    books = books || this.state.books || [];
    shareUsers = shareUsers || this.state.shareUsers || [];
    
    // Determine selected book
    const urlBookId = (urlState && urlState.bookId) || '';
    const savedBookId = Application.getSelectedRecipeBook();
    let selectedBookId = urlBookId || savedBookId || (books[0] ? String(books[0].id) : '');
    let selectedBook = books.find((b) => String(b.id) === String(selectedBookId));
    
    // Handle special values like __all_books__
    if (selectedBookId === '__all_books__') {
      selectedBook = { id: '__all_books__', name: 'All Books', is_owner: true };
    }
    
    // If URL book ID doesn't exist, fall back to saved selection or first book
    if (urlBookId && !selectedBook && books.length > 0) {
      const fallbackId = savedBookId && savedBookId !== '__all_books__' ? savedBookId : String(books[0].id);
      selectedBookId = fallbackId;
      selectedBook = books.find((b) => String(b.id) === String(fallbackId)) || books[0];
    }
    
    // If saved book ID doesn't exist (book was deleted), fall back to first book
    if (!selectedBook && books.length > 0) {
      selectedBookId = String(books[0].id);
      selectedBook = books[0];
    }
    
    const currentView = (urlState && urlState.view) || 'home';
    const selectedCategoryId = (urlState && urlState.categoryId) || '';
    const searchQuery = (urlState && urlState.searchQuery) || '';
    const recipeId = (urlState && urlState.recipeId) || '';

    // Preserve existing recipes/categoryRecipes when navigating within same book
    const isSameBook = this.state.selectedBookId === selectedBookId;
    // When returning to home view, we'll reload recipes — don't preserve stale ones
    const preserveRecipes = isSameBook && currentView !== 'home';
    // Don't preserve categoryRecipes when returning to home view — it'll be rebuilt from fresh data
    const preserveCategoryRecipes = isSameBook && currentView !== 'home';

    this.setState({
      books,
      shareUsers,
      selectedBookId,
      selectedBook,
      editBookName: selectedBook?.name || '',
      editBookDesc: selectedBook?.description || '',
      currentView,
      selectedCategoryId,
      searchQuery,
      categoryRecipes: preserveCategoryRecipes ? this.state.categoryRecipes : {},
      recipes: preserveRecipes ? this.state.recipes : [],
    });

      if (selectedBookId && selectedBook) {
        if (recipeId) {
          // Recipe detail view — fetch full recipe
          this.setState({ isLoading: true, customServings: null, effectiveServings: null, showMetric: false, checkedIngredients: new Set(), activeDirectionStep: -1, heroTextColor: '#fff' });
          getRecipeDetail(recipeId).then((res) => {
            const recipe = res.data;
            this.setState({ viewingRecipe: recipe, currentView: 'recipe', isLoading: false });
          }).catch(() => {
            this.setState({ isLoading: false });
          });
        } else if (currentView === 'category' && selectedCategoryId) {
          // Only load category recipes, skip all recipes
          this.loadRecipesPromise(selectedBookId, selectedCategoryId).then(() => {
            this.setState({ isLoading: false });
          });
        } else if (currentView === 'search' && searchQuery) {
          // Search directly - no need to load all recipes first
          searchRecipes(selectedBookId, searchQuery.trim(), selectedCategoryId || null)
            .then((res) => {
              const recipes = Array.isArray(res.data) ? res.data : (res.data?.data || []);
              this.setState({ recipes, isLoading: false });
            }).catch((err) => {
              console.error('Search failed:', err);
              this.setState({ recipes: [], isLoading: false });
            });
        } else {
          // Home view — load all recipes + categories
          // For __all_books__, pass null to get all categories across all books
          const categoriesBookId = selectedBookId === '__all_books__' ? null : selectedBookId;
          const catPromise = this.loadCategoriesPromise(categoriesBookId);
          const recipePromise = this.loadRecipesPromise(selectedBookId);
          Promise.allSettled([catPromise, recipePromise]).then(() => {
            this.updateCategoryRecipesFromLoaded();
            this.setState({ isLoading: false });
          });
        }
      } else {
        this.setState({ isLoading: false });
      }
  };

  // Check if a value looks like a UUID (8-4-4-4-12 hex format)
  isValidUuid = (val) => {
    return typeof val === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val);
  }

  loadRecipesPromise = (bookId, categoryId = null) => {
    const page = this.state.currentPage;
    // Prevent duplicate calls for the same page
    if (this._loadingRecipes && this._loadingBookId === bookId && this._loadingCategoryId === categoryId && this._loadingPage === page) {
      return Promise.resolve();
    }
    this._loadingRecipes = true;
    this._loadingBookId = bookId;
    this._loadingCategoryId = categoryId;
    this._loadingPage = page;

    // Validate bookId is a UUID or special value, otherwise fall back to first book
    if (bookId && bookId !== '__all_books__' && !this.isValidUuid(bookId)) {
      console.warn('[RB] Invalid bookId (not a UUID), falling back to first book:', bookId);
      const firstBook = this.state.books?.[0];
      if (firstBook) {
        bookId = firstBook.id;
      } else {
        return Promise.resolve([]);
      }
    }

    const promise = (bookId === '__all_books__' && !categoryId)
      ? getRecipesFromAllBooks(page).then((res) => {
          const recipes = Array.isArray(res.data) ? res.data : (res.data?.data || []);
          this.setState({ recipes, totalPages: res.data?.last_page || 1, recipeTotal: res.data?.total || recipes.length });
          return recipes;
        }).catch(() => [])
      : (bookId === '__all_books__' && categoryId && categoryId !== '__all__')
      ? getRecipesByCategory(null, categoryId, page).then((res) => {
          const recipes = Array.isArray(res.data) ? res.data : (res.data?.data || []);
          this.setState({ recipes, totalPages: res.data?.last_page || 1, recipeTotal: res.data?.total || recipes.length });
          return recipes;
        }).catch(() => [])
      : (categoryId === '__all__')
      ? this.loadAllRecipesInBook(bookId, page).then(() => [])
      : (categoryId && categoryId !== '__all__')
      ? getRecipesByCategory(bookId, categoryId, page).then((res) => {
          const recipes = Array.isArray(res.data) ? res.data : (res.data?.data || []);
          this.setState({ recipes, totalPages: res.data?.last_page || 1, recipeTotal: res.data?.total || recipes.length });
          return recipes;
        }).catch(() => [])
      : getRecipesByBook(bookId, page).then((res) => {
          const recipes = Array.isArray(res.data) ? res.data : (res.data?.data || []);
          this.setState({ recipes, totalPages: res.data?.last_page || 1, recipeTotal: res.data?.total || recipes.length });
          return recipes;
        }).catch(() => []);

    promise.finally(() => {
      this._loadingRecipes = false;
      this._loadingBookId = null;
      this._loadingCategoryId = null;
      this._loadingPage = null;
    });

    return promise;
  }

  loadCategoriesPromise = (bookId = null) => {
    // Validate bookId is a UUID or null, otherwise fall back
    if (bookId && bookId !== '__all_books__' && !this.isValidUuid(bookId)) {
      console.warn('[RB] Invalid bookId for categories, falling back to first book:', bookId);
      const firstBook = this.state.books?.[0];
      bookId = firstBook ? firstBook.id : null;
    }
    const params = bookId ? { params: { recipe_book_id: bookId } } : {};
    return getCategories(params).then((res) => {
      const categories = res.data || [];
      if (!bookId) {
        const withCount = categories.filter(c => c.recipe_count > 0).length;
        console.log('[RB] categories (global):', { total: categories.length, withCount });
      }
      this.setState({ categories });
      return categories;
    }).catch(() => []);
  }

  // ===== All Books List View (Progressive Loading) =====

  loadAllBooksList = (page = 1) => {
    if (this.state.allBooksListLoading) return;
    if (page === 1) {
      this.setState({
        allBooksListRecipes: [],
        allBooksListPage: 1,
        allBooksListLoading: true,
        allBooksListHasMore: true,
        allBooksListTotal: 0,
      });
    } else {
      this.setState({ allBooksListLoading: true });
    }

    const p = getRecipesFromAllBooksList(page).then((res) => {
      const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      const total = res.data?.total || 0;
      const lastPage = res.data?.last_page || 1;

      this.setState((prev) => ({
        allBooksListRecipes: page === 1 ? data : [...prev.allBooksListRecipes, ...data],
        allBooksListPage: page,
        allBooksListLoading: false,
        allBooksListHasMore: page < lastPage,
        allBooksListTotal: total,
      }));

      // Preload next batch if we have more
      if (page < lastPage && page <= 5) {
        // Auto-load up to 50 (5 batches of 10)
        setTimeout(() => this.loadAllBooksList(page + 1), 300);
      }
    }).catch(() => {
      this.setState({ allBooksListLoading: false });
    });
    return p;
  }

  // Progressive list loading for "All Recipes" within a single book
  loadAllRecipesInBook = (bookId, page = 1) => {
    if (page === 1) {
      this.setState({
        allBooksListRecipes: [],
        allBooksListPage: 1,
        allBooksListLoading: true,
        allBooksListHasMore: true,
        allBooksListTotal: 0,
      });
    } else {
      this.setState({ allBooksListLoading: true });
    }

    const promise = getRecipesByBook(bookId, { page, per_page: 10 }).then((res) => {
      const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      const total = res.data?.total || 0;
      const lastPage = res.data?.last_page || 1;

      this.setState((prev) => ({
        allBooksListRecipes: page === 1 ? data : [...prev.allBooksListRecipes, ...data],
        allBooksListPage: page,
        allBooksListLoading: false,
        allBooksListHasMore: page < lastPage,
        allBooksListTotal: total,
        recipes: page === 1 ? data : [...prev.allBooksListRecipes, ...data],
        recipeTotal: total,
        totalPages: lastPage,
      }));

      // Auto-load in chunks of 5 pages (50 recipes per chunk)
      // When page % 5 === 0, stop auto-loading and wait for user to scroll again
      if (page < lastPage && page % 5 !== 0) {
        setTimeout(() => this.loadAllRecipesInBook(bookId, page + 1), 300);
      }
    }).catch(() => {
      this.setState({ allBooksListLoading: false });
    });
    return promise;
  };

  // Load more when scrolling (infinite scroll)
  loadMoreAllBooks = () => {
    if (this.state.allBooksListLoading || !this.state.allBooksListHasMore) return;
    const nextPage = this.state.allBooksListPage + 1;
    this.loadAllRecipesInBook(this.state.selectedBookId, nextPage);
  }

  // Prevent duplicate calls to loadRecipes/loadCategories
  loadRecipes = (bookId, categoryId = null) => {
    if (this._loadingRecipes) return;
    return this.loadRecipesPromise(bookId, categoryId);
  }

  loadCategories = (bookId = null) => {
    return this.loadCategoriesPromise(bookId);
  }

  // Called after both recipes AND categories have loaded to populate category recipes cache
  // Uses setTimeout to ensure React has flushed all pending setState calls
  updateCategoryRecipesFromLoaded = () => {
    setTimeout(() => {
      const { recipes, categories } = this.state;
      console.log('[RB] updateCategoryRecipesFromLoaded:', { recipesLength: recipes.length, categoriesLength: categories.length, selectedBookId: this.state.selectedBookId });
      if (recipes.length === 0 || categories.length === 0) return;
      const catRecipes = this.deriveCategoryRecipes(recipes, categories);
      catRecipes['__all__'] = recipes;
      console.log('[RB] derived categoryRecipes keys:', Object.keys(catRecipes).filter(k => k !== '__all__').length, 'categories with recipes');
      this.setState({ categoryRecipes: catRecipes });
    }, 0);
  }

  resetPagination = () => {
    this.setState({ currentPage: 1 });
  }

  renderPagination = () => {
    if (this.state.totalPages <= 1) return null;
    const onPageChange = (newPage) => {
      this.setState({ currentPage: newPage }, () => {
        this.loadRecipes(this.state.selectedBookId, this.state.selectedCategoryId === '__all__' ? null : this.state.selectedCategoryId)
          .then(() => this.updateCategoryRecipesFromLoaded());
      });
    };
    return (
      <div className="rk-pagination">
        <Button size="small" disabled={this.state.currentPage <= 1} onClick={() => onPageChange(this.state.currentPage - 1)}>← Previous</Button>
        <span className="rk-page-indicator">Page {this.state.currentPage} of {this.state.totalPages}</span>
        <Button size="small" disabled={this.state.currentPage >= this.state.totalPages} onClick={() => onPageChange(this.state.currentPage + 1)}>Next →</Button>
      </div>
    );
  }

  // Navigate to home view
  navigateHome = () => {
    // Set URL immediately with correct home state (before setState updates)
    this.setUrlState(this.state.selectedBookId, 'home', '', '', this.state.viewingRecipe ? '' : '');
    this.setState({
      currentView: 'home',
      selectedCategoryId: '',
      searchQuery: '',
      recipes: [],
      categoryRecipes: {}, // Clear stale category recipes - will be rebuilt from fresh data
      currentPage: 1,
      isLoading: true,
    }, () => {
      if (this.state.selectedBookId) {
        this.loadCategoriesPromise(this.state.selectedBookId).then(() => {
          this.loadRecipesPromise(this.state.selectedBookId).then(() => {
            this.updateCategoryRecipesFromLoaded();
            this.setState({ isLoading: false });
          });
        });
      } else {
        this.setState({ isLoading: false });
      }
    });
  }

  // Navigate to category view
  navigateToCategory = (categoryId) => {
    this.setState({
      currentView: 'category',
      selectedCategoryId: categoryId,
      currentPage: 1,
      recipes: [], // Clear to prevent showing all recipes from home view
      isLoading: true,
    }, () => {
      // pushState doesn't trigger popstate, so load data directly
      if (this.state.selectedBookId) {
        this.loadRecipesPromise(this.state.selectedBookId, categoryId).then(() => {
          this.setState({ isLoading: false });
        });
      }
      this.saveState();
    });
  }

  // Navigate to search view
  navigateToSearch = (query) => {
    this.setState({
      currentView: 'search',
      searchQuery: query,
      currentPage: 1,
      recipes: [],
      isLoading: true,
    }, () => {
      // pushState doesn't trigger popstate, so load data directly
      if (query && this.state.selectedBookId && this.state.selectedBookId !== '__all_books__') {
        searchRecipes(this.state.selectedBookId, query.trim(), this.state.selectedCategoryId || null)
          .then((res) => {
            const recipes = Array.isArray(res.data) ? res.data : (res.data?.data || []);
            this.setState({ recipes, isLoading: false });
          }).catch((err) => {
            console.error('Search failed:', err);
            this.setState({ recipes: [], isLoading: false });
            showToast('Search failed. Please try again.', { severity: 'error' });
          });
      } else if (this.state.selectedBookId === '__all_books__') {
        // Search all books not supported yet - show message
        this.setState({ recipes: [], isLoading: false });
        showToast('Search is available within a specific recipe book.', { severity: 'warning' });
      } else {
        this.setState({ recipes: [], isLoading: false });
      }
      this.saveState();
    });
  }

  // Get the currently selected category object
  getSelectedCategory = () => {
    return this.state.categories.find(c => String(c.id) === String(this.state.selectedCategoryId));
  }

  handleSearch = () => {
    const { searchQuery, selectedBookId, selectedCategoryId } = this.state;
    if (!searchQuery.trim() || !selectedBookId) return;

    this.setState({ isLoading: true });
    searchRecipes(selectedBookId, searchQuery.trim(), selectedCategoryId || null)
      .then((res) => {
        // Laravel pagination wraps results in { data: [...], total, per_page, ... }
        const recipes = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        this.setState({ recipes, isLoading: false });
      })
      .catch(() => {
        this.setState({ isLoading: false });
      });
  }

  clearSearch = () => {
    // Set URL immediately with correct home state (before setState updates)
    this.setUrlState(this.state.selectedBookId, 'home', '', '', '');
    this.setState({
      searchQuery: '',
      selectedCategoryId: '',
      currentView: 'home',
      recipes: [],
      currentPage: 1,
      isLoading: true,
    }, () => {
      const book = this.getSelectedBook();
      if (book) {
        this.loadRecipes(book.id).then(() => {
          this.updateCategoryRecipesFromLoaded();
        });
      }
      this.setState({ isLoading: false });
    });
  }

  // Determine text color for hero overlay based on image brightness
  getHeroTextColor = (imgEl) => {
    if (!imgEl || !imgEl.naturalWidth) return '#fff';
    try {
      const canvas = document.createElement('canvas');
      canvas.width = imgEl.naturalWidth;
      canvas.height = imgEl.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(imgEl, 0, 0);
      const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      let total = 0;
      for (let i = 0; i < data.length; i += 4) {
        total += (data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114);
      }
      const avg = total / (data.length / 4);
      return avg > 128 ? '#000' : '#fff';
    } catch {
      return '#fff';
    }
  }

  getSelectedBook = () => {
    return this.state.books.find((book) => String(book.id) === String(this.state.selectedBookId));
  }

  selectBook = (bookId) => {
    const book = this.state.books.find((b) => String(b.id) === String(bookId));
    const isAllBooks = bookId === '__all_books__';
    this.setState({
      selectedBookId: bookId,
      selectedBook: isAllBooks ? { id: '__all_books__', name: 'All Books', is_owner: true } : book,
      editBookName: book?.name || '',
      editBookDesc: book?.description || '',
      editBookAllowSharedEdit: book?.allow_shared_edit || false,
      selectedShareUserId: '',
      selectedCategoryId: '',
      searchQuery: '',
      currentView: 'home',
      categoryRecipes: {},
      categoryImageIndex: {},
    }, () => {
      if (bookId && bookId !== '__new__') {
        // Persist selection to localStorage
        Application.setSelectedRecipeBook(bookId);
        // Only send to backend if it's a real book ID (not a special value like __all_books__)
        if (bookId !== '__all_books__') {
          updateSelectedRecipeBook(bookId).catch(() => {});
        }

        const categoriesBookId = isAllBooks ? null : bookId;
        const recipePromise = this.loadRecipesPromise(bookId);
        const catPromise = this.loadCategoriesPromise(categoriesBookId);
        Promise.allSettled([catPromise, recipePromise]).then(() => {
          this.updateCategoryRecipesFromLoaded();
        });
        this.saveState();
      }
    });
  }

  // Convert image path to correct URL (handles relative paths and localhost URLs)
  getImageUrl = (imageUrl) => {
    if (!imageUrl) return '';
    // If it's already a full URL, just ensure it's https
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl.replace('http://', 'https://');
    }
    // For relative paths, prepend the backend host
    const backendHost = HOST.endsWith('/api') ? HOST.slice(0, -4) : HOST.replace(/\/api$/, '');
    return backendHost + (imageUrl.startsWith('/') ? imageUrl : '/' + imageUrl);
  }

  // Get thumbnail URL (small, fast-loading version)
  getThumbUrl = (imageUrl) => {
    if (!imageUrl) return '';
    const fullUrl = this.getImageUrl(imageUrl);
    // Convert recipe-image/ to recipe-image-thumb/
    return fullUrl.replace('/recipe-image/', '/recipe-image-thumb/');
  }

  handleCreateBook = () => {
    const name = this.state.newBookName.trim();
    if (!name) return;

    this.setState({ isLoading: true });
    createRecipeBook({ name, description: this.state.newBookDesc.trim() })
      .then(() => {
        this.setState({ newBookName: '', newBookDesc: '' });
        this.loadData();
      })
      .catch(() => {
        this.setState({ isLoading: false });
      });
  }

  handleUpdateBook = () => {
    const book = this.getSelectedBook();
    const name = this.state.editBookName.trim();
    if (!book || !name) return;

    this.setState({ isLoading: true });
    updateRecipeBook({ id: book.id, name, description: this.state.editBookDesc.trim(), allow_shared_edit: this.state.editBookAllowSharedEdit })
      .then(() => {
        this.loadData();
      })
      .catch(() => {
        this.setState({ isLoading: false });
      });
  }

  handleDeleteBook = () => {
    const book = this.state.bookToDelete;
    if (!book) return;

    this.setState({ isLoading: true });
    deleteRecipeBook(book.id)
      .then(() => {
        showToast('Recipe book deleted', { severity: 'success' });
        this.setState({ showDeleteModal: false, bookToDelete: null });
        this.loadData();
      })
      .catch(() => {
        this.setState({ isLoading: false });
      });
  }

  handleShareBook = () => {
    const book = this.getSelectedBook();
    if (!book || !this.state.selectedShareUserId) return;

    this.setState({ isLoading: true });
    shareRecipeBook({ recipe_book_id: book.id, target_user_id: this.state.selectedShareUserId })
      .then(() => {
        showToast('Recipe book shared', { severity: 'success' });
        this.setState({ selectedShareUserId: '', showShareModal: false });
        this.loadData();
      })
      .catch(() => {
        this.setState({ isLoading: false });
      });
  }

  handleUnshareBook = (userId) => {
    const book = this.getSelectedBook();
    if (!book || !userId) return;

    this.setState({ isLoading: true });
    unshareRecipeBook({ recipe_book_id: book.id, target_user_id: userId })
      .then(() => {
        showToast('Sharing removed', { severity: 'success' });
        this.loadData();
      })
      .catch(() => {
        this.setState({ isLoading: false });
      });
  }

  // ===== SHARE LINK =====

  openShareLink = (recipe) => {
    this.setState({ showShareLinkModal: true, shareLinkUrl: recipe.share_url || '', shareLinkLoading: !recipe.share_url });
    if (!recipe.share_url) {
      generateShareToken(recipe.id).then((res) => {
        this.setState({ shareLinkUrl: res.data.share_url, shareLinkLoading: false });
      }).catch(() => {
        this.setState({ shareLinkLoading: false });
        showToast('Failed to generate share link', { severity: 'error' });
      });
    }
  }

  copyShareLink = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(this.state.shareLinkUrl).then(() => {
        showToast('Link copied to clipboard', { severity: 'success' });
      });
    } else {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = this.state.shareLinkUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      showToast('Link copied to clipboard', { severity: 'success' });
    }
  }

  revokeShareLink = (recipeId) => {
    this.setState({ shareLinkLoading: true });
    revokeShareToken(recipeId).then(() => {
      this.setState({ shareLinkUrl: '', shareLinkLoading: false });
      showToast('Share link revoked', { severity: 'success' });
      // Refresh the viewing recipe
      if (this.state.viewingRecipe && String(this.state.viewingRecipe.id) === String(recipeId)) {
        getRecipeDetail(recipeId).then((res) => {
          this.setState({ viewingRecipe: res.data });
        });
      }
    }).catch(() => {
      this.setState({ shareLinkLoading: false });
      showToast('Failed to revoke share link', { severity: 'error' });
    });
  }

  // ===== RECIPE MANAGEMENT =====

  openAddRecipe = () => {
    this.resetRecipeForm();
    this.setState({ showAddRecipeModal: true });
  }

  resetRecipeForm = () => {
    this.setState({
      recipeTitle: '',
      recipeDirection: '',
      recipeServings: '',
      recipePrepTime: '',
      recipeCookTime: '',
      recipeDifficulty: 'easy',
      recipeNotes: '',
      recipeIngredients: [{ name: '', quantity: '', unit: '', notes: '' }],
      recipeCategories: [],
      showCategoryPicker: false,
    });
  }

  handleAddRecipe = () => {
    const book = this.getSelectedBook();
    const title = this.state.recipeTitle.trim();
    if (!book || !title) return;

    const ingredients = this.state.recipeIngredients
      .filter(i => i.name.trim())
      .map(i => {
        const { _customUnit, ...rest } = i;
        const unit = i.unit === '__custom__'
          ? (_customUnit && _customUnit.trim() && _customUnit !== '__custom__' ? _customUnit.trim() : '')
          : i.unit;
        return { ...rest, unit };
      });

    this.setState({ isLoading: true });
    addRecipe({
      recipe_book_id: book.id,
      title,
      directions: this.state.recipeDirection.trim(),
      servings: this.state.recipeServings ? parseInt(this.state.recipeServings) : null,
      prep_time: this.state.recipePrepTime.trim() || null,
      cook_time: this.state.recipeCookTime.trim() || null,
      difficulty: this.state.recipeDifficulty,
      notes: this.state.recipeNotes.trim() || null,
      ingredients: ingredients.length > 0 ? ingredients : undefined,
      categories: this.state.recipeCategories.length > 0 ? this.state.recipeCategories : undefined,
    })
      .then((res) => {
        showToast('Recipe added', { severity: 'success' });
        this.setState({ showAddRecipeModal: false });
        this.resetRecipeForm();
        this.loadRecipes(book.id).then(() => {
          this.updateCategoryRecipesFromLoaded();
        });
      })
      .catch(() => {
        this.setState({ isLoading: false });
      });
  }

  handleUpdateRecipe = () => {
    const book = this.getSelectedBook();
    const title = this.state.recipeTitle.trim();
    if (!this.state.editingRecipeId || !book || !title) return;

    // Filter out empty ingredient rows completely
    const ingredients = this.state.recipeIngredients
      .filter(i => i.name && i.name.trim().length > 0)
      .map(i => {
        const { _customUnit, ...rest } = i;
        const unit = i.unit === '__custom__'
          ? (_customUnit && _customUnit.trim() && _customUnit !== '__custom__' ? _customUnit.trim() : '')
          : (i.unit ? i.unit.trim() : '');
        return { ...rest, unit };
      });

    this.setState({ isLoading: true });
    updateRecipe({
      id: this.state.editingRecipeId,
      recipe_book_id: book.id,
      title,
      directions: this.state.recipeDirection.trim(),
      servings: this.state.recipeServings ? parseInt(this.state.recipeServings) : null,
      prep_time: this.state.recipePrepTime.trim() || null,
      cook_time: this.state.recipeCookTime.trim() || null,
      difficulty: this.state.recipeDifficulty,
      notes: this.state.recipeNotes.trim() || null,
      ingredients: ingredients.length > 0 ? ingredients : [],
      categories: this.state.recipeCategories.length > 0 ? this.state.recipeCategories : [],
    })
      .then((res) => {
        showToast('Recipe updated', { severity: 'success' });
        // Update the recipe in local state immediately with fresh data from API
        const updatedRecipes = this.state.recipes.map(r => String(r.id) === String(res.data.id) ? res.data : r);
        // Also update viewingRecipe if we're currently viewing this recipe
        const updatedViewingRecipe = String(this.state.viewingRecipe?.id) === String(res.data.id) ? res.data : this.state.viewingRecipe;
        this.setState({ 
          recipes: updatedRecipes, 
          viewingRecipe: updatedViewingRecipe,
          showAddRecipeModal: false, 
          editingRecipeId: null,
          isLoading: false,
          customServings: null,
          effectiveServings: null,
          showMetric: false,
          checkedIngredients: new Set(),
          activeDirectionStep: -1,
          heroTextColor: '#fff',
        });
        this.resetRecipeForm();
      })
      .catch((err) => {
        this.setState({ isLoading: false });
        const errorMsg = err.response?.data?.message || 'Failed to update recipe. Please try again.';
        showToast(errorMsg, { severity: 'error' });
      });
  }

  handleDeleteRecipe = (recipeId) => {
    this.setState({ isLoading: true, showDeleteModal: false });
    deleteRecipe(recipeId)
      .then(() => {
        showToast('Recipe deleted', { severity: 'success' });
        this.closeRecipeDetail();
        const book = this.getSelectedBook();
        if (book) {
          this.loadRecipes(book.id).then(() => {
            this.updateCategoryRecipesFromLoaded();
          });
        }
      })
      .catch(() => {
        this.setState({ isLoading: false });
      });
  }

  // ===== URL IMPORT =====

  previewRecipeFromUrl = () => {
    const url = this.state.importUrl.trim();
    if (!url) return;

    this.setState({ isLoading: true });
    importRecipeByUrl({ url })
      .then((res) => {
        const data = res.data;
        this.setState({
          importedPreview: data,
          showImportUrlModal: false,
          isLoading: false,
        });
      })
      .catch((err) => {
        const msg = err.response?.data?.message || 'Failed to fetch recipe from URL.';
        showToast(msg, { severity: 'error' });
        this.setState({ isLoading: false });
      });
  }

  confirmImportUrl = () => {
    const preview = this.state.importedPreview;
    const book = this.getSelectedBook();
    if (!preview || !book) return;

    this.setState({ isLoading: true });
    addRecipe({
      recipe_book_id: book.id,
      title: preview.title,
      description: preview.description || null,
      image_url: preview.image_url || null,
      directions: preview.directions || '',
      servings: preview.servings,
      prep_time: preview.prep_time || null,
      cook_time: preview.cook_time || null,
      difficulty: preview.difficulty || 'easy',
      notes: preview.source_url ? `Source: ${preview.source_url}` : null,
      ingredients: preview.ingredients || [],
    })
      .then(() => {
        showToast('Recipe imported successfully!', { severity: 'success' });
        this.setState({ importedPreview: null, importUrl: '' });
        this.loadRecipes(book.id).then(() => {
          this.updateCategoryRecipesFromLoaded();
        });
      })
      .catch(() => {
        this.setState({ isLoading: false });
      });
  }

  // ===== TEXT IMPORT =====

  handleImportText = () => {
    const text = this.state.importText.trim();
    const book = this.getSelectedBook();
    if (!text || !book) return;

    this.setState({ isLoading: true });
    importRecipeByText({ text, recipe_book_id: book.id })
      .then((res) => {
        showToast('Recipe imported from text!', { severity: 'success' });
        this.setState({ importText: '', showImportTextModal: false });
        this.loadRecipes(book.id).then(() => {
          this.updateCategoryRecipesFromLoaded();
        });
      })
      .catch((err) => {
        const msg = err.response?.data?.message || 'Failed to import recipe from text.';
        showToast(msg, { severity: 'error' });
        this.setState({ isLoading: false });
      });
  }

  // ===== HTML IMPORT =====

  handleImportHtml = () => {
    const book = this.getSelectedBook();
    if (!this.state.htmlFile || !book) return;

    this.setState({ importHtmlLoading: true });
    const formData = new FormData();
    formData.append('html_file', this.state.htmlFile);
    formData.append('recipe_book_id', book.id);

    importRecipeHtml(formData)
      .then((res) => {
        showToast(res.data.message || 'Recipes imported!', { severity: 'success' });
        this.setState({ htmlFile: null, showImportHtmlModal: false, importHtmlLoading: false });
        this.loadRecipes(book.id).then(() => {
          this.updateCategoryRecipesFromLoaded();
        });
      })
      .catch((err) => {
        this.setState({ importHtmlLoading: false });
        showToast(err.response?.data?.message || 'Failed to import HTML file.', { severity: 'error' });
      });
  }

  // ===== IMAGE UPLOAD =====

  handleUploadRecipeImage = (recipeId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/jpg,image/gif';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;

      this.setState({ isLoading: true });
      const formData = new FormData();
      formData.append('recipe_id', recipeId);
      formData.append('image', file);

      uploadRecipeImage(formData)
        .then((res) => {
          showToast('Image uploaded!', { severity: 'success' });
          this.loadRecipes(this.getSelectedBook()?.id).then(() => {
            this.updateCategoryRecipesFromLoaded();
          });
        })
        .catch((err) => {
          showToast(err.response?.data?.message || 'Failed to upload image.', { severity: 'error' });
        })
        .finally(() => {
          this.setState({ isLoading: false });
        });
    };
    input.click();
  }

  // ===== VIEW RECIPE DETAIL =====

  openRecipeDetail = (recipe) => {
    this.setState({ 
      currentView: 'recipe', 
      viewingRecipe: null, // Don't show anything until we have full data
      isLoading: true, 
      customServings: null, 
      effectiveServings: null, 
      showMetric: false,
      checkedIngredients: new Set(), 
      activeDirectionStep: -1, 
      heroTextColor: '#fff', 
      metricOverrides: {}, 
      metricContextMenu: null 
    }, () => {
      if (!recipe.id) {
        console.error('[RB] Cannot view recipe - recipe.id is null/undefined:', recipe);
        this.setState({ isLoading: false });
        showToast('Recipe ID is missing. Please refresh the page.', { severity: 'error' });
        return;
      }
      this.saveState(recipe.id);
      getRecipeDetail(recipe.id).then((res) => {
        this.setState({ viewingRecipe: res.data, isLoading: false });
      }).catch(() => {
        this.setState({ isLoading: false });
      });
    });
  }

  openEditRecipe = (recipe) => {
    // Use the modal form instead of inline editing
    const knownUnits = [
      '', 'cups', 'tbsp', 'tsp', 'oz', 'lb', 'g', 'kg', 'ml', 'l', 'fl oz',
      'pint', 'quart', 'gal', 'pinch', 'dash', 'cup', 'tablespoon', 'teaspoon',
      'ounce', 'pound', 'gram', 'kilogram', 'milliliter', 'liter', 'fluid ounce',
      'whole', 'slice', 'clove', 'piece', 'can', 'bunch', 'sprig', 'to taste'
    ];
    const ingredients = (recipe.ingredients && recipe.ingredients.length > 0)
      ? recipe.ingredients.map(i => {
          const unit = i.unit || '';
          if (knownUnits.includes(unit)) {
            return { name: i.name || '', quantity: (i.quantity || '').toString(), unit, notes: i.notes || '' };
          }
          // Custom unit not in dropdown — mark as __custom__ with the actual value in _customUnit
          return { name: i.name || '', quantity: (i.quantity || '').toString(), unit: '__custom__', _customUnit: unit, notes: i.notes || '' };
        })
      : [{ name: '', quantity: '', unit: '', notes: '' }];

    this.setState({
      editingRecipeId: recipe.id,
      recipeTitle: recipe.title || '',
      recipeDirection: recipe.directions || '',
      recipeServings: recipe.servings?.toString() || '',
      recipePrepTime: recipe.prep_time || '',
      recipeCookTime: recipe.cook_time || '',
      recipeDifficulty: recipe.difficulty || 'easy',
      recipeNotes: recipe.notes || '',
      recipeIngredients: ingredients,
      recipeCategories: recipe.categories ? recipe.categories.map(c => c.id) : [],
      showAddRecipeModal: true,
    });
  }

  saveEditRecipe = () => {
    // Alias — the modal form uses handleUpdateRecipe directly
    this.handleUpdateRecipe();
  }

  cancelEditRecipe = () => {
    this.setState({ showAddRecipeModal: false, editingRecipeId: null });
  }

  closeRecipeDetail = () => {
    history.back();
  }

  // ===== SERVING SCALE =====
  adjustServings = (delta) => {
    const recipe = this.state.viewingRecipe;
    if (!recipe?.servings) return;
    const base = recipe.servings;
    const current = this.state.effectiveServings !== null ? this.state.effectiveServings : base;
    // Smart increment: use 1 for small recipes, scale up for larger ones
    const step = base <= 4 ? 1 : (base <= 8 ? 2 : 4);
    let newEffective = current + delta * step;
    newEffective = Math.max(Math.ceil(base * 0.25) * 0.5, Math.min(base * 8, newEffective));
    newEffective = Math.round(newEffective * 2) / 2;
    this.setState({ effectiveServings: newEffective, customServings: null });
  }

  setCustomServings = (value) => {
    this.setState({ customServings: value === '' ? null : parseFloat(value) });
  }

  applyCustomServings = () => {
    const custom = this.state.customServings;
    const recipe = this.state.viewingRecipe;
    if (!custom || !recipe?.servings) return;
    const base = recipe.servings;
    const scale = custom / base;
    if (scale >= 0.25 && scale <= 8) {
      this.setState({ effectiveServings: custom, customServings: null });
    }
  }

  resetServings = () => {
    this.setState({ effectiveServings: null, customServings: null });
  }

  setServingsMultiplier = (multiplier) => {
    const recipe = this.state.viewingRecipe;
    if (!recipe?.servings) return;
    const base = recipe.servings;
    const target = Math.round(base * multiplier * 2) / 2;
    const scale = target / base;
    if (scale >= 0.25 && scale <= 8) {
      this.setState({ effectiveServings: target, customServings: null });
    }
  }

  getServingScale = () => {
    const recipe = this.state.viewingRecipe;
    if (!recipe?.servings || !this.state.effectiveServings) return 1;
    return this.state.effectiveServings / recipe.servings;
  }

  // Get a display string for the scale multiplier
  getScaleLabel = () => {
    const scale = this.getServingScale();
    if (scale === 1) return '';
    if (scale === 0.5) return '½x';
    if (scale === 2) return '2x';
    if (scale === 0.25) return '¼x';
    if (scale === 4) return '4x';
    return `${scale.toFixed(1)}x`;
  }

  // Unit conversion table (all to base unit: teaspoons)
  _unitToTeaspoons = {
    'cup': 48, 'cups': 48,
    'tablespoon': 3, 'tablespoons': 3, 'tbsp': 3,
    'teaspoon': 1, 'teaspoons': 1, 'tsp': 1,
    'ounce': 6, 'ounces': 6, 'oz': 6,
    'pint': 96, 'pints': 96,
    'quart': 192, 'quarts': 192,
    'gallon': 768, 'gallons': 768,
    'liter': 202.88, 'liters': 202.88,
    'milliliter': 0.203, 'milliliters': 0.203, 'ml': 0.203,
  };

  // Metric conversion tables
  _volumeToMl = {
    'cup': 236.588, 'cups': 236.588,
    'tablespoon': 14.787, 'tablespoons': 14.787, 'tbsp': 14.787,
    'teaspoon': 4.929, 'teaspoons': 4.929, 'tsp': 4.929,
    'fluid ounce': 29.574, 'fluid ounces': 29.574, 'fl oz': 29.574, 'oz': 29.574,
    'pint': 473.176, 'pints': 473.176,
    'quart': 946.353, 'quarts': 946.353,
    'gallon': 3785.41, 'gallons': 3785.41,
    'liter': 1000, 'liters': 1000,
    'milliliter': 1, 'milliliters': 1, 'ml': 1,
  };

  _weightToGrams = {
    'ounce': 28.3495, 'ounces': 28.3495, 'oz': 28.3495,
    'pound': 453.592, 'pounds': 453.592, 'lb': 453.592, 'lbs': 453.592,
    'gram': 1, 'grams': 1, 'g': 1,
    'kilogram': 1000, 'kilograms': 1000, 'kg': 1000,
  };

  // Dry ingredient weight per cup in grams (approximate) — only truly dry/powdered/granular ingredients
  _dryIngredientGramsPerCup = {
    'flour': 120, 'all-purpose flour': 120, 'bread flour': 127, 'cake flour': 110, 'whole wheat flour': 120,
    'sugar': 200, 'white sugar': 200, 'granulated sugar': 200, 'brown sugar': 220, 'packed brown sugar': 220,
    'powdered sugar': 115, 'confectioners sugar': 115, 'icing sugar': 115,
    'cornstarch': 115, 'corn starch': 115,
    'baking powder': 120, 'baking soda': 120,
    'salt': 270, 'sea salt': 270, 'kosher salt': 230, 'table salt': 270,
    'pepper': 60, 'black pepper': 60, 'ground pepper': 60,
    'spice': 60, 'spices': 60, 'cinnamon': 85, 'nutmeg': 80, 'ginger': 65, 'cumin': 85, 'paprika': 55, 'chili powder': 60, 'cayenne': 45, 'turmeric': 60, 'oregano': 55, 'basil': 55, 'thyme': 55, 'sage': 50, 'rosemary': 50, 'parsley': 55, 'dill': 50, 'mint': 50, 'cloves': 90, 'cardamom': 70, 'allspice': 70, 'mace': 65,
    'butter': 227, 'margarine': 227,
    'cheese': 220, 'grated cheese': 100, 'shredded cheese': 110, 'parmesan': 100, 'mozzarella': 110, 'cheddar': 110,
    'nuts': 180, 'almonds': 190, 'walnuts': 140, 'pecans': 135, 'cashews': 150, 'peanuts': 160, 'pistachios': 140, 'hazelnuts': 120,
    'oats': 90, 'rolled oats': 90, 'quick oats': 80, 'oatmeal': 90,
    'rice': 190, 'white rice': 190, 'brown rice': 185, 'jasmine rice': 190, 'basmati rice': 185,
    'pasta': 150, 'spaghetti': 150, 'penne': 150, 'macaroni': 150, 'fettuccine': 150, 'linguine': 150,
    'breadcrumbs': 125, 'bread crumbs': 125, 'panko': 60,
    'coconut': 130, 'shredded coconut': 100, 'desiccated coconut': 110,
    'cocoa': 120, 'cocoa powder': 120, 'chocolate': 170, 'chocolate chips': 160, 'baking chocolate': 170,
    'yeast': 120, 'active dry yeast': 120, 'instant yeast': 115,
    'cornmeal': 140, 'polenta': 140,
    'semolina': 160,
    'shortening': 200,
    'crushed graham crackers': 90, 'graham cracker crumbs': 90,
    'croutons': 60,
    'raisins': 140, 'dried fruit': 130, 'cranberries': 130, 'currants': 130,
    'beans': 200, 'lentils': 190, 'chickpeas': 200, 'kidney beans': 200, 'black beans': 200,
    'peanut butter': 250, 'almond butter': 250,
    'yogurt': 245, 'sour cream': 230, 'cream cheese': 225,
    'eggs': 50, 'egg': 50,
  };

  // Liquid ingredient weight per cup in ml (for volume→ml conversion)
  _liquidIngredientMlPerCup = {
    'milk': 240, 'cream': 230, 'heavy cream': 230, 'whipping cream': 230,
    'water': 240, 'broth': 240, 'stock': 240, 'chicken broth': 240, 'beef broth': 240,
    'oil': 218, 'olive oil': 218, 'vegetable oil': 218, 'canola oil': 218, 'coconut oil': 218,
    'vinegar': 240, 'rice vinegar': 240, 'balsamic vinegar': 240,
    'lemon juice': 240, 'lime juice': 240,
    'soy sauce': 240, 'worcestershire sauce': 240, 'hot sauce': 240, 'ketchup': 240, 'mustard': 240, 'mayonnaise': 240,
    'honey': 240, 'maple syrup': 240, 'molasses': 240, 'corn syrup': 240,
    'jam': 240, 'jelly': 240, 'marmalade': 240,
    'vanilla extract': 240, 'almond extract': 240,
  };

  // Check if ingredient name matches a dry ingredient (volume→grams conversion)
  _isDryIngredient = (name) => {
    if (!name) return false;
    const lower = name.toLowerCase().trim();
    // Exact match first
    if (this._dryIngredientGramsPerCup[lower]) return true;
    // Check if any key is contained in the ingredient name
    for (const key of Object.keys(this._dryIngredientGramsPerCup)) {
      if (lower.includes(key)) return true;
    }
    return false;
  };

  // Get grams per cup for a dry ingredient
  _getGramsPerCup = (name) => {
    if (!name) return 120; // default for unknown dry ingredients
    const lower = name.toLowerCase().trim();
    // Exact match first
    if (this._dryIngredientGramsPerCup[lower]) return this._dryIngredientGramsPerCup[lower];
    // Find best matching key
    for (const key of Object.keys(this._dryIngredientGramsPerCup)) {
      if (lower.includes(key)) return this._dryIngredientGramsPerCup[key];
    }
    return 120;
  };

  // Convert volume unit to grams for dry ingredients
  _volumeToGramsForDry = (qty, unit, ingredientName) => {
    const unitLower = unit.toLowerCase();
    const gramsPerCup = this._getGramsPerCup(ingredientName);
    // Convert unit to cups first
    let cups;
    if (unitLower === 'cup' || unitLower === 'cups') cups = qty;
    else if (unitLower === 'tablespoon' || unitLower === 'tablespoons' || unitLower === 'tbsp') cups = qty / 16;
    else if (unitLower === 'teaspoon' || unitLower === 'teaspoons' || unitLower === 'tsp') cups = qty / 48;
    else if (unitLower === 'fluid ounce' || unitLower === 'fluid ounces' || unitLower === 'fl oz') cups = qty / 8;
    else if (unitLower === 'pint' || unitLower === 'pints') cups = qty * 2;
    else if (unitLower === 'quart' || unitLower === 'quarts') cups = qty * 4;
    else if (unitLower === 'gallon' || unitLower === 'gallons') cups = qty * 16;
    else if (unitLower === 'liter' || unitLower === 'liters') cups = qty * 4.22675;
    else if (unitLower === 'milliliter' || unitLower === 'milliliters' || unitLower === 'ml') cups = qty / 236.588;
    else cups = qty; // fallback
    return cups * gramsPerCup;
  };

  _isVolumeUnit = (unit) => {
    return !!this._volumeToMl[unit.toLowerCase()];
  };

  _isWeightUnit = (unit) => {
    return !!this._weightToGrams[unit.toLowerCase()];
  };

  _convertToMetric = (qty, unit) => {
    const unitLower = unit.toLowerCase();
    if (this._isVolumeUnit(unitLower)) {
      const ml = qty * this._volumeToMl[unitLower];
      // Format: use L for >= 1000ml, otherwise ml
      if (ml >= 1000) {
        const liters = ml / 1000;
        return `${this._formatMetricNumber(liters)} L`;
      }
      return `${this._formatMetricNumber(ml)} ml`;
    }
    if (this._isWeightUnit(unitLower)) {
      const grams = qty * this._weightToGrams[unitLower];
      // Format: use kg for >= 1000g, otherwise g
      if (grams >= 1000) {
        const kg = grams / 1000;
        return `${this._formatMetricNumber(kg)} kg`;
      }
      return `${this._formatMetricNumber(grams)} g`;
    }
    return null; // not convertible
  };

  _formatMetricNumber = (num) => {
    if (Number.isInteger(num)) return num.toString();
    // Round to reasonable precision
    if (num >= 100) return Math.round(num).toString();
    if (num >= 10) return Math.round(num * 10) / 10;
    return Math.round(num * 100) / 100;
  };

  _parseFraction = (rawNum, fractions) => {
    let normalized = rawNum;
    for (const [frac, val] of Object.entries(fractions)) {
      if (normalized.includes(frac)) normalized = normalized.replace(frac, val.toString());
    }
    return parseFloat(normalized);
  };

  _scaleToMetric = (originalQty, unit, ingredientName, overrideMode, scale, fractions) => {
    const isDry = ingredientName ? this._isDryIngredient(ingredientName) : false;
    // Determine effective mode: override > auto-detect
    const forceGrams = overrideMode === 'grams';
    const forceMl = overrideMode === 'ml';
    const useGrams = forceGrams || (!forceMl && isDry);

    // Handle combined quantities like "1 cup and 3 tablespoons"
    if (typeof originalQty === 'string' && originalQty.includes(' and ')) {
      const parts = originalQty.split(/\s+and\s+/);
      let totalMl = 0;
      let totalGrams = 0;

      for (const part of parts) {
        const match = part.trim().match(/^([\d¼½¾⅓⅔⅛⅜⅝⅞]+(?:\s+[\d\/\.]+)?)\s+(.+)$/);
        if (!match) continue;

        let rawNum = match[1];
        const partUnit = match[2].trim().toLowerCase();

        let num = this._parseFraction(rawNum, fractions);
        if (isNaN(num)) continue;

        const scaled = num * scale;

        // Convert to metric
        if (this._isVolumeUnit(partUnit)) {
          if (useGrams) {
            // Convert volume to grams (dry or forced)
            totalGrams += this._volumeToGramsForDry(scaled, partUnit, ingredientName);
          } else {
            // Convert volume to ml (liquid or forced)
            totalMl += scaled * this._volumeToMl[partUnit];
          }
        } else if (this._isWeightUnit(partUnit)) {
          totalGrams += scaled * this._weightToGrams[partUnit];
        }
      }

      // Format combined metric result
      const results = [];
      if (totalMl > 0) {
        results.push(totalMl >= 1000 ? `${this._formatMetricNumber(totalMl / 1000)} L` : `${this._formatMetricNumber(totalMl)} ml`);
      }
      if (totalGrams > 0) {
        results.push(totalGrams >= 1000 ? `${this._formatMetricNumber(totalGrams / 1000)} kg` : `${this._formatMetricNumber(totalGrams)} g`);
      }
      return results.join(' and ');
    }

    // Handle single quantity with a separate unit param
    if (unit) {
      const unitLower = unit.toLowerCase();
      let num = this._parseFraction(String(originalQty), fractions);
      if (!isNaN(num)) {
        const scaled = num * scale;

        // For dry ingredients with volume units, convert to grams instead of ml
        if (useGrams && this._isVolumeUnit(unitLower)) {
          const grams = this._volumeToGramsForDry(scaled, unitLower, ingredientName);
          if (grams >= 1000) return `${this._formatMetricNumber(grams / 1000)} kg`;
          return `${this._formatMetricNumber(grams)} g`;
        }

        const metric = this._convertToMetric(scaled, unitLower);
        if (metric) return metric;
      }
    }

    // Try to parse unit from the quantity string itself
    if (typeof originalQty === 'string') {
      const match = originalQty.match(/^([\d¼½¾⅓⅔⅛⅜⅝⅞]+(?:\s+[\d\/\.]+)?)\s*(.+)?$/);
      if (match && match[2]) {
        let num = this._parseFraction(match[1], fractions);
        if (!isNaN(num)) {
          const scaled = num * scale;
          const unitFromStr = match[2].trim().toLowerCase();

          if (useGrams && this._isVolumeUnit(unitFromStr)) {
            const grams = this._volumeToGramsForDry(scaled, unitFromStr, ingredientName);
            if (grams >= 1000) return `${this._formatMetricNumber(grams / 1000)} kg`;
            return `${this._formatMetricNumber(grams)} g`;
          }

          const metric = this._convertToMetric(scaled, unitFromStr);
          if (metric) return metric;
        }
      }
    }

    // Fallback: just scale the number if no unit
    if (typeof originalQty === 'string') {
      let num = this._parseFraction(String(originalQty), fractions);
      if (!isNaN(num)) {
        const scaled = num * scale;
        return this._formatMetricNumber(scaled);
      }
    }

    // Final fallback
    return originalQty;
  };

  scaleQuantity = (originalQty, unit, ingredientName, idx) => {
    const scale = this.getServingScale();
    if (!originalQty) return originalQty;

    const fractions = { '¼': 0.25, '½': 0.5, '¾': 0.75, '⅓': 0.33, '⅔': 0.67, '⅛': 0.125, '⅜': 0.375, '⅝': 0.625, '⅞': 0.875 };

    const overrideMode = idx !== undefined ? this.getMetricMode(idx) : 'auto';

    // Check if this ingredient has a per-ingredient override that conflicts with global mode
    if (this.state.showMetric) {
      // Global metric mode — but if override is 'imperial', show original
      if (overrideMode === 'imperial') {
        if (scale === 1) return originalQty;
        // Scale but keep imperial
        return this._scaleToImperial(originalQty, unit, ingredientName, scale, fractions);
      }
      // Convert to metric
      return this._scaleToMetric(originalQty, unit, ingredientName, overrideMode, scale, fractions);
    }

    // Global imperial mode — but if override is 'grams' or 'ml', convert to that
    if ((overrideMode === 'grams' || overrideMode === 'ml') && idx !== undefined) {
      return this._scaleToMetric(originalQty, unit, ingredientName, overrideMode, scale, fractions);
    }

    // No change needed at scale=1
    if (scale === 1) return originalQty;

    // Handle combined quantities like "1 cup and 3 tablespoons" — combine into single scaled value
    if (typeof originalQty === 'string' && originalQty.includes(' and ')) {
      const parts = originalQty.split(/\s+and\s+/);
      let totalTeaspoons = 0;
      let primaryUnit = null;
      let primaryTeaspoonsPerUnit = 0;

      for (const part of parts) {
        const match = part.trim().match(/^([\d¼½¾⅓⅔⅛⅜⅝⅞]+(?:\s+[\d\/\.]+)?)\s+(.+)$/);
        if (!match) continue;

        let rawNum = match[1];
        const unit = match[2].trim().toLowerCase();

        // Normalize fractions
        let normalized = rawNum;
        for (const [frac, val] of Object.entries(fractions)) {
          if (normalized.includes(frac)) normalized = normalized.replace(frac, val.toString());
        }

        // Parse the number
        let num = parseFloat(normalized);
        if (isNaN(num)) continue;

        // Convert to teaspoons
        const tspPerUnit = this._unitToTeaspoons[unit] || 1;
        totalTeaspoons += num * tspPerUnit;

        // Track the largest unit as primary
        if (tspPerUnit > primaryTeaspoonsPerUnit) {
          primaryUnit = match[2].trim();
          primaryTeaspoonsPerUnit = tspPerUnit;
        }
      }

      if (primaryUnit && primaryTeaspoonsPerUnit > 0) {
        // Convert total teaspoons back to primary unit
        const totalInPrimary = (totalTeaspoons / primaryTeaspoonsPerUnit) * scale;
        return this._formatScaledQuantity(totalInPrimary, primaryUnit, fractions);
      }

      // Fallback: scale each part separately
      const scaledParts = parts.map(part => this._scaleSingleQuantity(part.trim(), scale, fractions));
      return scaledParts.join(' and ');
    }

    // Scale the number only — unit is rendered separately by renderIngredientAmount
    return this._scaleQuantityNumber(String(originalQty), scale, fractions);
  }

  // Parse a quantity string, scale it, and return only the formatted number (no unit)
  _scaleQuantityNumber = (qtyStr, scale, fractions) => {
    let rawNum = qtyStr.trim();

    // Normalize fractions
    let normalized = rawNum;
    for (const [frac, val] of Object.entries(fractions)) {
      if (normalized.includes(frac)) {
        normalized = normalized.replace(frac, val.toString());
      }
    }

    // Parse the number
    let num = 0;
    const mixedMatch = normalized.match(/^(\d+)\s+([\d.]+\/[\d.]+)$/);
    if (mixedMatch) {
      num = parseFloat(mixedMatch[1]) + (parseFloat(mixedMatch[2].split('/')[0]) / parseFloat(mixedMatch[2].split('/')[1]));
    } else {
      const numMatch = normalized.match(/^([\d\s\/\.]+)/);
      if (!numMatch) return qtyStr;
      if (numMatch[1].includes('/')) {
        const parts = numMatch[1].trim().split('/');
        if (parts.length === 2) num = parseFloat(parts[0]) / parseFloat(parts[1]);
        else num = parseFloat(numMatch[1].trim());
      } else {
        num = parseFloat(numMatch[1].trim());
      }
    }

    if (isNaN(num)) return qtyStr;
    const scaled = num * scale;

    // Format the scaled number (same logic as _scaleSingleQuantity but without the unit)
    let scaledStr;
    if (scaled < 0.1) scaledStr = scaled.toFixed(2);
    else if (scaled < 1) scaledStr = scaled.toFixed(2);
    else if (Number.isInteger(scaled)) scaledStr = scaled.toString();
    else {
      const whole = Math.floor(scaled);
      const frac = scaled - whole;
      if (frac > 0.25 && frac < 0.75) {
        const fracMap = { 0.25: '¼', 0.33: '⅓', 0.5: '½', 0.67: '⅔', 0.75: '¾' };
        for (const [threshold, sym] of Object.entries(fracMap)) {
          if (Math.abs(frac - parseFloat(threshold)) < 0.08) {
            scaledStr = whole > 0 ? `${whole}${sym}` : sym;
            break;
          }
        }
      }
      if (!scaledStr) scaledStr = scaled % 1 === 0 ? scaled.toString() : scaled.toFixed(1);
    }

    return scaledStr;
  }

  _formatScaledQuantity = (scaled, unit, fractions) => {
    // Format the scaled number with fraction support
    let scaledStr;
    if (scaled < 0.1) scaledStr = scaled.toFixed(2);
    else if (scaled < 1) scaledStr = scaled.toFixed(2);
    else if (Number.isInteger(scaled)) scaledStr = scaled.toString();
    else {
      const whole = Math.floor(scaled);
      const frac = scaled - whole;
      const fracMap = { 0.25: '¼', 0.33: '⅓', 0.5: '½', 0.67: '⅔', 0.75: '¾', 0.125: '⅛', 0.375: '⅜', 0.625: '⅝', 0.875: '⅞' };
      for (const [threshold, sym] of Object.entries(fracMap)) {
        if (Math.abs(frac - parseFloat(threshold)) < 0.08) {
          scaledStr = whole > 0 ? `${whole}${sym}` : sym;
          break;
        }
      }
      if (!scaledStr) scaledStr = scaled % 1 === 0 ? scaled.toString() : scaled.toFixed(1);
    }

    // Handle pluralization
    const scaledNum = parseFloat(scaledStr);
    const pluralizedUnit = (scaledNum !== 1 && scaledNum !== 0)
      ? unit.replace(/^(cup|tablespoon|teaspoon|ounce|pound|gram|bunch|head|stick|sprig|stalk|pinch|bottle|jar|package|leaf|drop|slice|piece|pot|can|glass|bowl|box)$/i, '$1s')
      : unit;

    return `${scaledStr} ${pluralizedUnit}`;
  }

  // Toggle metric display
  toggleMetric = () => {
    this.setState(prev => ({ showMetric: !prev.showMetric }));
  }

  // Show metric unit context menu for an ingredient
  showMetricContextMenu = (idx, x, y) => {
    this._skipClickEvents = 3; // skip pointerdown, pointerup, click from long-press release
    this._longPressIngredientIdx = idx; // suppress ingredient's onClick

    // Adjust position to keep menu on-screen
    const menuWidth = 220;
    const menuHeight = 200;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (x + menuWidth > vw) x = vw - menuWidth - 8;
    if (y + menuHeight > vh) y = vh - menuHeight - 8;
    if (x < 8) x = 8;
    if (y < 8) y = 8;

    this.setState({ metricContextMenu: { idx, x, y } });
  };

  // Hide metric context menu
  hideMetricContextMenu = () => {
    this.setState({ metricContextMenu: null });
  };

  // Copy ingredient text to clipboard
  copyIngredient = (idx) => {
    const ing = this.state.viewingRecipe.ingredients[idx];
    if (!ing) return;
    const scaledQty = ing.quantity ? this.scaleQuantity(ing.quantity, ing.unit, ing.name, idx) : null;
    let text = '';
    if (scaledQty) text += `${scaledQty}`;
    if (ing.unit && !this.state.showMetric) text += ` ${ing.unit}`;
    text += ` ${ing.name}`;
    if (ing.notes) text += ` (${ing.notes})`;
    navigator.clipboard.writeText(text.trim()).then(() => {
      this.hideMetricContextMenu();
    });
  };

  // Set metric/imperial override for an ingredient (auto/grams/ml/imperial)
  setMetricOverride = (idx, mode) => {
    const overrides = { ...this.state.metricOverrides };
    if (mode === 'auto') {
      delete overrides[idx];
    } else {
      overrides[idx] = mode; // 'grams', 'ml', or 'imperial'
    }
    this.setState({ metricOverrides: overrides });
  };

  // Get the effective metric mode for an ingredient
  getMetricMode = (idx) => {
    return this.state.metricOverrides[idx] || 'auto';
  };

  // Get the toggle text to show next to the unit (what the alternative would be)
  _getUnitToggleText = (idx, mode) => {
    const ing = this.state.viewingRecipe?.ingredients?.[idx];
    if (!ing || !ing.unit) return '';

    if (this.state.showMetric) {
      // In metric mode — always show the alternative
      if (mode === 'imperial') {
        // Showing imperial, alternative is metric
        return ` (${this._isDryIngredient(ing.name) ? 'g' : 'ml'})`;
      }
      // Showing metric, show alternative metric unit
      if (mode === 'grams') return ' (ml)';
      if (mode === 'ml') return ' (g)';
      // Auto mode showing metric
      return ` (${this._isDryIngredient(ing.name) ? 'g' : 'ml'})`;
    }

    // In imperial mode — only show toggle if this ingredient is overridden to metric
    if (mode === 'grams' || mode === 'ml') {
      return ` (${escapeHtml(ing.unit)})`;
    }
    // Auto in imperial mode — no toggle needed, it's already showing the default
    return '';
  };

  // Render ingredient amount with metric toggle support
  renderIngredientAmount = (idx, ing) => {
    if (!ing.quantity) return null;
    const scaledQty = this.scaleQuantity(ing.quantity, ing.unit, ing.name, idx);
    const mode = this.getMetricMode(idx);
    const toggleText = this._getUnitToggleText(idx, mode);
    
    return (
      <span>
        {scaledQty}
        {ing.unit && <span>{this.state.showMetric && (mode === 'grams' || mode === 'ml') ? ' ' + (mode === 'grams' ? 'g' : 'ml') : ' ' + escapeHtml(ing.unit)}</span>}
        {toggleText && <span className="rk-ingredient-unit-toggle" onClick={(e) => { e.stopPropagation(); this._cycleMetricMode(idx); }}>{toggleText}</span>}
      </span>
    );
  };

  // Cycle through metric modes for an ingredient (auto -> grams -> ml -> imperial -> auto)
  _cycleMetricMode = (idx) => {
    const mode = this.getMetricMode(idx);
    if (this.state.showMetric) {
      // In metric mode: auto -> grams -> ml -> imperial -> auto
      const modes = ['auto', 'grams', 'ml', 'imperial'];
      const currentIdx = modes.indexOf(mode);
      const nextMode = modes[(currentIdx + 1) % modes.length];
      if (nextMode === 'auto') {
        this.setMetricOverride(idx, 'auto');
      } else {
        this.setMetricOverride(idx, nextMode);
      }
    } else {
      // In imperial mode: auto -> grams -> ml -> auto
      const modes = ['auto', 'grams', 'ml'];
      const currentIdx = modes.indexOf(mode);
      const nextMode = modes[(currentIdx + 1) % modes.length];
      if (nextMode === 'auto') {
        this.setMetricOverride(idx, 'auto');
      } else {
        this.setMetricOverride(idx, nextMode);
      }
    }
  };

  // Scale a quantity while keeping imperial units (for when override is 'imperial' but global is metric)
  _scaleToImperial = (originalQty, unit, ingredientName, scale, fractions) => {
    if (scale === 1) return originalQty;

    // Handle combined quantities like "1 cup and 3 tablespoons"
    if (typeof originalQty === 'string' && originalQty.includes(' and ')) {
      const parts = originalQty.split(/\s+and\s+/);
      let results = [];
      for (const part of parts) {
        const scaled = this._scaleQuantityNumber(part.trim(), scale, fractions);
        results.push(scaled);
      }
      return results.join(' and ');
    }

    return this._scaleQuantityNumber(originalQty, scale, fractions);
  };

  _scaleSingleQuantity = (qtyStr, scale, fractions) => {
    // Parse "1 cup", "2 tablespoons", "½ tsp" etc.
    const match = qtyStr.match(/^([\d¼½¾⅓⅔⅛⅜⅝⅞]+(?:\s+[\d\/\.]+)?)\s+(.+)$/);
    if (!match) return qtyStr;

    let rawNum = match[1];
    const unit = match[2];

    // Normalize fractions
    let normalized = rawNum;
    for (const [frac, val] of Object.entries(fractions)) {
      if (normalized.includes(frac)) {
        normalized = normalized.replace(frac, val.toString());
      }
    }

    // Parse the number
    let num = 0;
    const mixedMatch = normalized.match(/^(\d+)\s+([\d.]+\/[\d.]+)$/);
    if (mixedMatch) {
      num = parseFloat(mixedMatch[1]) + (parseFloat(mixedMatch[2].split('/')[0]) / parseFloat(mixedMatch[2].split('/')[1]));
    } else {
      const numMatch = normalized.match(/^([\d\s\/\.]+)/);
      if (!numMatch) return qtyStr;
      if (numMatch[1].includes('/')) {
        const parts = numMatch[1].trim().split('/');
        if (parts.length === 2) num = parseFloat(parts[0]) / parseFloat(parts[1]);
        else num = parseFloat(numMatch[1].trim());
      } else {
        num = parseFloat(numMatch[1].trim());
      }
    }

    if (isNaN(num)) return qtyStr;
    const scaled = num * scale;

    // Format the scaled number
    let scaledStr;
    if (scaled < 0.1) scaledStr = scaled.toFixed(2);
    else if (scaled < 1) scaledStr = scaled.toFixed(2);
    else if (Number.isInteger(scaled)) scaledStr = scaled.toString();
    else {
      const whole = Math.floor(scaled);
      const frac = scaled - whole;
      if (frac > 0.25 && frac < 0.75) {
        const fracMap = { 0.25: '¼', 0.33: '⅓', 0.5: '½', 0.67: '⅔', 0.75: '¾' };
        for (const [threshold, sym] of Object.entries(fracMap)) {
          if (Math.abs(frac - parseFloat(threshold)) < 0.08) {
            scaledStr = whole > 0 ? `${whole}${sym}` : sym;
            break;
          }
        }
      }
      if (!scaledStr) scaledStr = scaled % 1 === 0 ? scaled.toString() : scaled.toFixed(1);
    }

    // Handle pluralization
    const scaledNum = parseFloat(scaledStr);
    const pluralizedUnit = (scaledNum !== 1 && scaledNum !== 0)
      ? unit.replace(/^(cup|tablespoon|teaspoon|ounce|pound|gram|bunch|head|stick|sprig|stalk|pinch|bottle|jar|package|leaf|drop|slice|piece|stick|pot|can|glass|bowl|box|leaf|leaf)$/i, '$1s')
      : unit;

    return `${scaledStr} ${pluralizedUnit}`;
  }

  // ===== INGREDIENT & DIRECTION TRACKING =====

  toggleIngredientChecked = (index) => {
    const checked = new Set(this.state.checkedIngredients);
    if (checked.has(index)) checked.delete(index);
    else checked.add(index);
    this.setState({ checkedIngredients: checked });
  }

  setActiveDirectionStep = (index) => {
    this.setState({ activeDirectionStep: this.state.activeDirectionStep === index ? -1 : index });
  }

  // ===== INGREDIENT LIST (DYNAMIC) =====

  updateIngredient = (index, field, value) => {
    const ingredients = this.state.recipeIngredients.map((ing, i) => {
      if (i !== index) return ing;
      const updated = { ...ing, [field]: value };
      // If selecting __custom__, show custom input with empty value.
      if (field === 'unit' && value === '__custom__') {
        updated._customUnit = '';
      }
      // If selecting a known unit from dropdown, clear custom unit.
      if (field === 'unit' && value !== '__custom__') {
        updated._customUnit = undefined;
      }
      // When typing in the custom unit field, keep unit as __custom__ and just store the value.
      return updated;
    });
    this.setState({ recipeIngredients: ingredients });
  }

  addIngredientRow = () => {
    this.setState({
      recipeIngredients: [...this.state.recipeIngredients, { name: '', quantity: '', unit: '', notes: '' }],
    });
  }

  removeIngredientRow = (index) => {
    const ingredients = this.state.recipeIngredients.filter((_, i) => i !== index);
    this.setState({ recipeIngredients: ingredients.length > 0 ? ingredients : [{ name: '', quantity: '', unit: '', notes: '' }] });
  }

  // ===== CATEGORY HANDLERS =====

  toggleCategorySelection = (categoryId) => {
    const categories = this.state.recipeCategories;
    if (categories.includes(categoryId)) {
      this.setState({ recipeCategories: categories.filter(id => id !== categoryId) });
    } else {
      this.setState({ recipeCategories: [...categories, categoryId] });
    }
  }

  handleFilterByCategory = (categoryId) => {
    this.setState({ selectedCategoryId: categoryId });
    const book = this.getSelectedBook();
    if (book) {
      this.loadRecipes(book.id, categoryId).then(() => {
        this.updateCategoryRecipesFromLoaded();
      });
    }
  }

  clearCategoryFilter = () => {
    this.setState({ selectedCategoryId: '' });
    const book = this.getSelectedBook();
    if (book) {
      this.loadRecipes(book.id).then(() => {
        this.updateCategoryRecipesFromLoaded();
      });
    }
  }

  // ===== RENDER HELPERS =====

  renderDifficultyBadge(difficulty) {
    if (!difficulty) return null;
    const colors = {
      easy: '#4caf50',
      medium: '#ff9800',
      hard: '#f44336',
      challenging: '#9c27b0',
    };
    return (
      <span className="difficulty-badge" style={{ backgroundColor: colors[difficulty] || '#666' }}>
        {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
      </span>
    );
  }

  render() {
    const { isLoading, books, selectedBook, recipes, shareUsers } = this.state;

    if (isLoading && books.length === 0) {
      return (
        <div className="recipe-book-page">
          <div className="loading-spinner"><CircularProgress /></div>
        </div>
      );
    }

    return (
      <div className="recipe-book-page">
        <div className="recipe-book-container">
          {/* CREATE NEW BOOK - only show if no books exist */}
          {books.length === 0 && (
            <div className="create-book-section">
              <TextField
                placeholder="Book name..."
                value={this.state.newBookName}
                onChange={(e) => this.setState({ newBookName: e.target.value })}
                size="small"
                sx={{ minWidth: 180 }}
              />
              <TextField
                placeholder="Description..."
                value={this.state.newBookDesc}
                onChange={(e) => this.setState({ newBookDesc: e.target.value })}
                size="small"
                sx={{ minWidth: 180 }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={this.handleCreateBook}
                startIcon={<FontAwesomeIcon icon={faPlus} />}
              >
                Create
              </Button>
            </div>
          )}

          {/* MAIN CONTENT */}
          <div className="recipe-book-main">
            <div className="recipe-book-content" ref={(el) => { this._contentEl = el; }}>
              {selectedBook || this.state.selectedBookId === '__new__' ? (
                <>
                  {this.state.currentView !== 'recipe' && (
                  <>
                  {/* Inline new book form when "__new__" is selected */}
                  {this.state.selectedBookId === '__new__' && (
                    <div className="inline-new-book-form">
                      <TextField
                        placeholder="New book name..."
                        value={this.state.newBookName}
                        onChange={(e) => this.setState({ newBookName: e.target.value })}
                        size="small"
                        autoFocus
                      />
                      <TextField
                        placeholder="Description..."
                        value={this.state.newBookDesc}
                        onChange={(e) => this.setState({ newBookDesc: e.target.value })}
                        size="small"
                      />
                      <Button variant="contained" size="small" onClick={this.handleCreateBook}>Create</Button>
                    </div>
                  )}

                  {selectedBook && (
                  <>
                  {/* Book actions (Share/Delete) */}
                  <div className="book-header-actions" style={{ marginBottom: 8 }}>
                    {selectedBook.is_owner && (
                      <>
                        <Button size="small" onClick={() => this.setState({ showShareModal: true })} startIcon={<FontAwesomeIcon icon={faShareAlt} />}>Share</Button>
                        <Button size="small" color="error" onClick={() => { this.setState({ showDeleteModal: true, bookToDelete: selectedBook }); }} startIcon={<FontAwesomeIcon icon={faTrash} />}>Delete</Button>
                      </>
                    )}
                  </div>

                  {/* EDIT NAME/DESCRIPTION - toggleable */}
                  {this.state.showEditBookForm ? (
                    <div className="book-edit-inline">
                      <TextField size="small" value={this.state.editBookName} onChange={(e) => this.setState({ editBookName: e.target.value })} placeholder="Book name..." sx={{ maxWidth: 200 }} />
                      <TextField size="small" value={this.state.editBookDesc} onChange={(e) => this.setState({ editBookDesc: e.target.value })} placeholder="Description..." sx={{ maxWidth: 250, ml: 1 }} />
                      <FormControlLabel control={<Checkbox size="small" checked={this.state.editBookAllowSharedEdit} onChange={(e) => this.setState({ editBookAllowSharedEdit: e.target.checked })} />} label="Allow shared users to edit" sx={{ ml: 1, fontSize: 13 }} />
                      <Button size="small" onClick={this.handleUpdateBook}>Save</Button>
                      <Button size="small" onClick={() => this.setState({ showEditBookForm: false })}>Cancel</Button>
                    </div>
                  ) : (
                    <div className="rk-action-bar">
                      <Button size="small" onClick={() => this.setState({ showEditBookForm: true })}>Edit Book Info</Button>
                    </div>
                  )}

                  {/* SHARED USERS */}
                  {selectedBook.shared_users && selectedBook.shared_users.length > 0 && (
                    <div className="shared-users-bar">
                      <span style={{ marginRight: 8, opacity: 0.7 }}>{selectedBook.is_owner ? 'Shared with:' : 'Shared by:'}</span>
                      {selectedBook.shared_users.map((user) => (
                        <span key={user.id} className="shared-user-chip">
                          {escapeHtml(user.name || user.uid)}
                          {selectedBook.is_owner && (
                            <span
                              className="remove-shared"
                              onClick={() => this.handleUnshareBook(user.id)}
                            >&times;</span>
                          )}
                        </span>
                      ))}
                    </div>
                  )}
                  </>
                  )}
                  </>
                  )}

                  {/* ADD/IMPORT MENU */}
                  {this.state.showAddMenu && (
                    <div className="rk-add-menu-overlay" onClick={() => this.setState({ showAddMenu: false })}>
                      <div className="rk-add-menu" onClick={e => e.stopPropagation()}>
                        <button className="rk-add-menu-item" onClick={() => { this.openAddRecipe(); this.setState({ showAddMenu: false }); }}>
                          <FontAwesomeIcon icon={faPlus} /> Add Recipe
                        </button>
                        <button className="rk-add-menu-item" onClick={() => { this.setState({ showImportUrlModal: true, showAddMenu: false }); }}>
                          <FontAwesomeIcon icon={faGlobe} /> From URL
                        </button>
                        <button className="rk-add-menu-item" onClick={() => { this.setState({ showImportTextModal: true, showAddMenu: false }); }}>
                          <FontAwesomeIcon icon={faPaste} /> Paste Text
                        </button>
                        <button className="rk-add-menu-item" onClick={() => { this.setState({ showImportHtmlModal: true, showAddMenu: false }); }}>
                          <FontAwesomeIcon icon={faCopy} /> Import HTML
                        </button>
                      </div>
                    </div>
                  )}

                  {/* RECIPE DETAIL PAGE - Full page view */}
                  {this.state.currentView === 'recipe' && (
                    this.state.isLoading ? (
                      <div className="loading-spinner" style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><CircularProgress /></div>
                    ) : this.state.viewingRecipe ? (
                      <div className="rk-recipe-page">
                        <div className="rk-recipe-hero">
                          {this.state.viewingRecipe.image_url ? (
                            <img src={this.getImageUrl(this.state.viewingRecipe.image_url)} alt={escapeHtml(this.state.viewingRecipe.title)} onLoad={(e) => this.setState({ heroTextColor: this.getHeroTextColor(e.target) })} />
                          ) : (
                            <div className="rk-recipe-hero-placeholder">
                              <FontAwesomeIcon icon={faUtensils} size="3x" />
                            </div>
                          )}
                          <div className="rk-recipe-hero-overlay">
                            <div className="rk-recipe-hero-bottom">
                              <h1 className="rk-recipe-hero-title" style={{ color: this.state.heroTextColor || '#fff' }}>{escapeHtml(this.state.viewingRecipe.title)}</h1>
                            </div>
                          </div>
                        </div>

                        {/* Recipe info bar */}
                        <div className="rk-recipe-info-bar">
                          {this.state.viewingRecipe.prep_time && (
                            <div className="rk-info-chip">
                              <FontAwesomeIcon icon={faClock} /> {escapeHtml(this.state.viewingRecipe.prep_time)}
                            </div>
                          )}
                          {this.state.viewingRecipe.cook_time && (
                            <div className="rk-info-chip">
                              <FontAwesomeIcon icon={faFire} /> {escapeHtml(this.state.viewingRecipe.cook_time)}
                            </div>
                          )}
                          {this.state.viewingRecipe.servings && (
                            <div className="rk-info-chip">
                              <FontAwesomeIcon icon={faUtensils} /> {this.state.effectiveServings !== null ? this.state.effectiveServings : escapeHtml(this.state.viewingRecipe.servings)} servings
                            </div>
                          )}
                          {this.state.viewingRecipe.difficulty && this.renderDifficultyBadge(this.state.viewingRecipe.difficulty)}
                        </div>

                        {/* Serving adjuster - always visible, inline */}
                        {this.state.viewingRecipe.servings && (
                          <div className="rk-serving-adjuster">
                            <div className="rk-serving-adjuster-left">
                              <span className="rk-serving-label">Servings</span>
                              {this.getScaleLabel() && <span className="rk-serving-scale-badge">{this.getScaleLabel()}</span>}
                            </div>
                            <div className="rk-serving-adjuster-controls">
                              <button className="rk-serving-stepper" onClick={() => this.adjustServings(-1)} aria-label="Decrease servings">
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M11 7H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                              </button>
                              <div className="rk-serving-input-wrapper">
                                <input
                                  className="rk-serving-input"
                                  type="number"
                                  min="1"
                                  max={this.state.viewingRecipe.servings * 8}
                                  value={this.state.customServings !== null ? this.state.customServings : (this.state.effectiveServings !== null ? this.state.effectiveServings : this.state.viewingRecipe.servings)}
                                  onChange={(e) => this.setCustomServings(e.target.value)}
                                  onFocus={(e) => e.target.select()}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      this.applyCustomServings();
                                    }
                                    if (e.key === 'Escape') {
                                      this.resetServings();
                                      e.target.blur();
                                    }
                                  }}
                                  onBlur={() => {
                                    if (this.state.customServings !== null) {
                                      this.applyCustomServings();
                                    }
                                  }}
                                />
                              </div>
                              <button className="rk-serving-stepper" onClick={() => this.adjustServings(1)} aria-label="Increase servings">
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 3V11M3 7H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                              </button>
                            </div>
                            <div className="rk-serving-presets">
                              <button
                                className={`rk-serving-preset${this.state.effectiveServings === this.state.viewingRecipe.servings * 0.5 ? ' rk-serving-preset-active' : ''}`}
                                onClick={() => this.setServingsMultiplier(0.5)}
                              >½</button>
                              <button
                                className={`rk-serving-preset${this.state.effectiveServings === null ? ' rk-serving-preset-active' : ''}`}
                                onClick={() => this.resetServings()}
                              >Orig</button>
                              <button
                                className={`rk-serving-preset${this.state.effectiveServings === this.state.viewingRecipe.servings * 2 ? ' rk-serving-preset-active' : ''}`}
                                onClick={() => this.setServingsMultiplier(2)}
                              >2×</button>
                            </div>
                          </div>
                        )}

                        {/* Metric toggle */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                          <span style={{ fontSize: 13, color: '#888' }}>Units:</span>
                          <button
                            className={`rk-metric-toggle-btn${this.state.showMetric ? ' rk-metric-active' : ''}`}
                            onClick={this.toggleMetric}
                          >
                            {this.state.showMetric ? 'Metric' : 'Imperial'}
                          </button>
                        </div>

                        {/* Description */}
                        {this.state.viewingRecipe.description && (
                          <div style={{ padding: '0 4px 12px', opacity: 0.85, lineHeight: 1.6, fontSize: 14 }}>
                            {escapeHtml(this.state.viewingRecipe.description)}
                          </div>
                        )}

                        {/* Ingredients & Directions */}
                        <div className="rk-recipe-body">
                          <div className="rk-ingredients-section">
                            <h2>Ingredients</h2>
                            <ul className="rk-ingredients-list">
                              {this.state.viewingRecipe.ingredients && this.state.viewingRecipe.ingredients.map((ing, idx) => (
                                <li
                                  key={idx}
                                  className={`rk-ingredient-item${this.state.checkedIngredients.has(idx) ? ' rk-ingredient-checked' : ''}`}
                                  onClick={(e) => {
                                    if (this._ingredientSkipClick) { this._ingredientSkipClick = false; return; }
                                    if (this._ingredientDragged) { this._ingredientDragged = false; return; }
                                    this.toggleIngredientChecked(idx);
                                  }}
                                  onContextMenu={(e) => { e.preventDefault(); this.showMetricContextMenu(idx, e.clientX, e.clientY); }}
                                  onMouseDown={(e) => {
                                    this._ingredientMouseDownX = e.clientX;
                                    this._ingredientMouseDownY = e.clientY;
                                    this._ingredientDragged = false;
                                    this._ingredientHoldTimer = setTimeout(() => {
                                      this._ingredientSkipClick = true;
                                      this.showMetricContextMenu(idx, e.clientX, e.clientY);
                                    }, 600);
                                  }}
                                  onMouseMove={(e) => {
                                    if (this._ingredientMouseDownX !== undefined) {
                                      const dx = e.clientX - this._ingredientMouseDownX;
                                      const dy = e.clientY - this._ingredientMouseDownY;
                                      if (Math.sqrt(dx * dx + dy * dy) > 10) {
                                        this._ingredientDragged = true;
                                        clearTimeout(this._ingredientHoldTimer);
                                      }
                                    }
                                  }}
                                  onMouseUp={() => {
                                    clearTimeout(this._ingredientHoldTimer);
                                    this._ingredientMouseDownX = undefined;
                                    this._ingredientMouseDownY = undefined;
                                  }}
                                  onMouseLeave={() => {
                                    clearTimeout(this._ingredientHoldTimer);
                                    this._ingredientMouseDownX = undefined;
                                    this._ingredientMouseDownY = undefined;
                                  }}
                                  onTouchStart={(e) => {
                                    this._ingredientTouchStartX = e.touches[0].clientX;
                                    this._ingredientTouchStartY = e.touches[0].clientY;
                                    this._ingredientDragged = false;
                                    this._ingredientTouchTimer = setTimeout(() => {
                                      this._ingredientTouchFired = true;
                                      this.showMetricContextMenu(idx, e.touches[0].clientX, e.touches[0].clientY);
                                    }, 600);
                                  }}
                                  onTouchEnd={(e) => {
                                    clearTimeout(this._ingredientTouchTimer);
                                    if (this._ingredientTouchFired) { this._ingredientTouchFired = false; return; }
                                    if (this._ingredientDragged) { this._ingredientDragged = false; return; }
                                    this.toggleIngredientChecked(idx);
                                    e.preventDefault();
                                  }}
                                  onTouchMove={(e) => {
                                    if (this._ingredientTouchStartX !== undefined) {
                                      const dx = e.touches[0].clientX - this._ingredientTouchStartX;
                                      const dy = e.touches[0].clientY - this._ingredientTouchStartY;
                                      if (Math.sqrt(dx * dx + dy * dy) > 10) {
                                        this._ingredientDragged = true;
                                        clearTimeout(this._ingredientTouchTimer);
                                      }
                                    }
                                  }}
                                >
                                  <span className="rk-ingredient-amount">
                                    {this.renderIngredientAmount(idx, ing)}
                                  </span>
                                  <span className="rk-ingredient-name">{escapeHtml(ing.name)}</span>
                                  {ing.notes && <span className="rk-ingredient-notes">{escapeHtml(ing.notes)}</span>}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className="rk-directions-section">
                            <h2>Directions</h2>
                            <div className="rk-directions-text">
                              {this.state.viewingRecipe.directions && this.state.viewingRecipe.directions.split('\n').filter(Boolean).map((step, idx) => (
                                <div 
                                  key={idx} 
                                  className={`rk-direction-step${this.state.activeDirectionStep === idx ? ' rk-direction-active' : ''}`}
                                  onClick={() => this.setActiveDirectionStep(idx)}
                                >
                                  <span className="rk-direction-number">{idx + 1}</span>
                                  {escapeHtml(step)}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null
                  )}

                  {/* MAIN CONTENT - Recipe Keeper Style */}
                  <div className="recipe-keeper-content">
                    {this.state.currentView !== 'recipe' && this.state.showSearchBar && (
                    <div className="rk-search-bar-expanded">
                      <TextField
                        placeholder="Search recipes..."
                        value={this.state.searchQuery}
                        onChange={(e) => this.setState({ searchQuery: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            if (this.state.searchQuery.trim()) {
                              this.navigateToSearch(this.state.searchQuery.trim());
                            }
                          }
                          if (e.key === 'Escape') {
                            this.setState({ showSearchBar: false });
                          }
                        }}
                        size="small"
                        fullWidth
                        autoFocus
                        InputProps={{
                          startAdornment: <FontAwesomeIcon icon={faSearch} style={{ marginRight: 8, opacity: 0.5 }} />,
                          endAdornment: (
                            <FontAwesomeIcon icon={faTimes} onClick={() => { this.setState({ searchQuery: '', showSearchBar: false }); }} style={{ cursor: 'pointer', opacity: 0.6 }} />
                          ),
                        }}
                      />
                    </div>
                    )}

                    {/* ALL RECIPES LIST VIEW (Progressive Loading) - shows when in "All Recipes" category */}
                    {this.state.currentView === 'category' && this.state.selectedCategoryId === '__all__' && (
                      <div className="rk-all-books-list" ref={(el) => { this._allBooksListEl = el; }}>
                        <div className="rk-list-header">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <Button size="small" onClick={this.navigateHome} startIcon={<FontAwesomeIcon icon={faArrowLeft} />}>Back</Button>
                            <h2 className="rk-view-title" style={{ margin: 0 }}>All Recipes</h2>
                          </div>
                          <span className="rk-list-count">{this.state.allBooksListTotal > 0 ? this.state.allBooksListTotal + ' recipes' : ''}</span>
                        </div>
                        <div className="rk-list-items">
                          {this.state.allBooksListLoading && this.state.allBooksListRecipes.length === 0 ? (
                            <div className="loading-spinner" style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><CircularProgress /></div>
                          ) : this.state.allBooksListRecipes.length === 0 ? (
                            <p className="rk-empty-state">No recipes found.</p>
                          ) : (
                            this.state.allBooksListRecipes.map((recipe) => {
                              const display = recipe; // getRecipesByBook already returns full data with categories, difficulty, etc.
                              return (
                                <div key={recipe.id} className="rk-list-item" onClick={() => this.openRecipeDetail(display || recipe)}>
                                  <div className="rk-list-item-image">
                                    {display && display.image_url ? (
                                      <div className="rk-image-blur-up">
                                        <img className="rk-image-thumb" src={this.getThumbUrl(display.image_url)} alt="" loading="lazy" />
                                        <img className="rk-image-full" src={this.getImageUrl(display.image_url)} alt={escapeHtml(display.title)} loading="lazy" onLoad={(e) => e.target.classList.add('loaded')} />
                                      </div>
                                    ) : recipe.image_url ? (
                                      <div className="rk-image-blur-up">
                                        <img className="rk-image-thumb" src={this.getThumbUrl(recipe.image_url)} alt="" loading="lazy" />
                                        <img className="rk-image-full" src={this.getImageUrl(recipe.image_url)} alt={escapeHtml(recipe.title)} loading="lazy" onLoad={(e) => e.target.classList.add('loaded')} />
                                      </div>
                                    ) : (
                                      <div className="rk-list-placeholder"><FontAwesomeIcon icon={faUtensils} /></div>
                                    )}
                                  </div>
                                  <div className="rk-list-item-info">
                                    <h4 className="rk-list-item-title">{escapeHtml(display?.title || recipe.title)}</h4>

                                    {display && display.categories && display.categories.length > 0 && (
                                      <div className="rk-recipe-cats">
                                        {display.categories.slice(0, 3).map((cat) => (
                                          <span key={cat.id} className="rk-cat-tag" style={{ backgroundColor: (cat.color || '#4caf50') + 'dd' }}>
                                            {escapeHtml(cat.name)}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                    <div className="rk-list-item-meta">
                                      {this.renderDifficultyBadge(display?.difficulty)}
                                      {display?.prep_time && <span className="rk-time-chip">Prep: {escapeHtml(display.prep_time)}</span>}
                                      {display?.cook_time && <span className="rk-time-chip">Cook: {escapeHtml(display.cook_time)}</span>}
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          )}
                          {/* Loading indicator for infinite scroll */}
                          {this.state.allBooksListLoading && this.state.allBooksListRecipes.length > 0 && (
                            <div className="rk-list-loading" style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
                              <CircularProgress size={24} />
                            </div>
                          )}
                          {!this.state.allBooksListHasMore && this.state.allBooksListRecipes.length > 0 && (
                            <p className="rk-empty-state" style={{ textAlign: 'center', opacity: 0.5, fontSize: 13 }}>
                              All {this.state.allBooksListTotal} recipes loaded
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* HOME VIEW - Category cards with rotating images */}
                    {this.state.currentView === 'home' && (
                      <div className="rk-home-view">
                        {/* "All" category card */}
                        <div
                          className="rk-category-card"
                          onClick={() => this.navigateToCategory('__all__')}
                        >
                          <div className="rk-cat-caption">
                            <span className="rk-cat-name">All Recipes</span>
                            <span className="rk-cat-count">{this.state.recipeTotal || this.state.recipes.length}</span>
                          </div>
                          <div className="rk-cat-image-single">
                            {(() => {
                              const catRecipes = this.state.categoryRecipes['__all__'] || [];
                              const withImages = catRecipes.filter(r => r.image_url && r.image_url.trim() !== '');
                              // If we have cached recipes with images, use rotating carousel
                              if (withImages.length > 0) {
                                const allIdx = this.state.categoryImageIndex['__all__'] ?? 0;
                                const r = withImages[allIdx % withImages.length];
                                return (
                                  <img
                                    src={this.getImageUrl(r.image_url)}
                                    alt={escapeHtml(r.title)}
                                    loading="lazy"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.parentElement.className = e.target.parentElement.className + ' rk-image-broken';
                                    }}
                                  />
                                );
                              }
                              // Fallback: check current recipes for any image
                              const currentWithImages = this.state.recipes.filter(r => r.image_url && r.image_url.trim() !== '');
                              if (currentWithImages.length > 0) {
                                const r = currentWithImages[0];
                                return (
                                  <img
                                    src={this.getImageUrl(r.image_url)}
                                    alt="All Recipes"
                                    loading="lazy"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.parentElement.className = e.target.parentElement.className + ' rk-image-broken';
                                    }}
                                  />
                                );
                              }
                              return <div className="rk-cat-empty">No recipes yet</div>;
                            })()}
                          </div>
                        </div>

                        {/* Category cards - only show if they have recipes */}
                        {this.state.categories
                          .filter(cat => {
                            const recipeCount = cat.recipe_count || 0;
                            const catRecipesLen = (this.state.categoryRecipes[cat.id] || []).length;
                            const hasRecipes = recipeCount > 0 || catRecipesLen > 0;
                            if (!hasRecipes && this.state.selectedBookId === '__all_books__') {
                              console.log('[RB] filtered out category:', cat.name, { recipeCount, catRecipesLen, id: cat.id });
                            }
                            return hasRecipes;
                          })
                          .map((cat) => {
                          const catRecipes = this.state.categoryRecipes[cat.id] || [];
                          const idx = this.state.categoryImageIndex[cat.id] ?? 0;
                          const currentRecipe = catRecipes[idx] || null;
                          if (this.state.selectedBookId === '__all_books__' && !this._debugLogged) {
                            this._debugLogged = true;
                            const passed = this.state.categories.filter(c => (c.recipe_count || 0) > 0 || (this.state.categoryRecipes[c.id] || []).length > 0).length;
                            console.log('[RB] category filter result:', { totalCategories: this.state.categories.length, passedFilter: passed, categoryRecipesKeys: Object.keys(this.state.categoryRecipes).length });
                          }
                          return (
                            <div
                              key={cat.id}
                              className="rk-category-card"
                              onClick={() => this.navigateToCategory(cat.id)}
                            >
                              <div className="rk-cat-caption">
                                <span className="rk-cat-name" style={{ color: cat.color }}>{cat.name}</span>
                                <span className="rk-cat-count">{cat.recipe_count || catRecipes.length || 0}</span>
                              </div>
                              <div className="rk-cat-image-single">
                                {(() => {
                                  const withImages = catRecipes.filter(cr => cr.image_url && cr.image_url.trim() !== '');
                                  if (withImages.length > 0) {
                                    const showRecipe = withImages[idx % withImages.length];
                                    if (showRecipe && showRecipe.image_url && showRecipe.image_url.trim() !== '') {
                                      return (
                                        <img
                                          src={this.getImageUrl(showRecipe.image_url)}
                                          alt={escapeHtml(cat.name)}
                                          loading="lazy"
                                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.parentElement.className = e.target.parentElement.className + ' rk-image-broken';
                                          }}
                                        />
                                      );
                                    }
                                  }
                                  // Fallback: use sample_image_url from the category API response
                                  if (cat.sample_image_url && cat.sample_image_url.trim() !== '') {
                                    return (
                                      <img
                                        src={this.getImageUrl(cat.sample_image_url)}
                                        alt={escapeHtml(cat.name)}
                                        loading="lazy"
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.parentElement.className = e.target.parentElement.className + ' rk-image-broken';
                                        }}
                                      />
                                    );
                                  }
                                  return <div className="rk-cat-placeholder"><FontAwesomeIcon icon={faUtensils} /></div>;
                                })()}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* CATEGORY VIEW (normal categories, 'All Recipes' uses list view) */}
                    {this.state.currentView === 'category' && this.state.selectedCategoryId !== '__all__' && (
                      <div className="rk-category-view">
                        <div className="rk-view-header">
                          <Button size="small" onClick={this.navigateHome} startIcon={<FontAwesomeIcon icon={faArrowLeft} />}>Back</Button>
                          <h2 className="rk-view-title">
                            {this.state.selectedCategoryId === '__all__' ? 'All Recipes' : this.getSelectedCategory()?.name || 'Category'}
                          </h2>
                        </div>

                        {/* Top Pagination */}
                        {this.renderPagination()}

                        {/* Recipe list */}
                        <div className="rk-recipe-list">
                          {this.state.isLoading && this.state.recipes.length === 0 ? (
                            <div className="loading-spinner" style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><CircularProgress /></div>
                          ) : this.state.recipes.length === 0 ? (
                            <p className="rk-empty-state">No recipes found.</p>
                          ) : (
                            this.state.recipes.map((recipe) => (
                              <div key={recipe.id} className="rk-recipe-item" onClick={() => this.openRecipeDetail(recipe)}>
                                <div className="rk-recipe-image">
                                  {recipe.image_url ? (
                                    <div className="rk-image-blur-up">
                                      <img className="rk-image-thumb" src={this.getThumbUrl(recipe.image_url)} alt="" loading="lazy" />
                                      <img className="rk-image-full" src={this.getImageUrl(recipe.image_url)} alt={escapeHtml(recipe.title)} loading="lazy" onLoad={(e) => e.target.classList.add('loaded')} />
                                    </div>
                                  ) : (
                                    <div className="rk-recipe-placeholder"><FontAwesomeIcon icon={faUtensils} /></div>
                                  )}
                                  <div className="rk-image-chips">
                                    {this.renderDifficultyBadge(recipe.difficulty)}
                                    {recipe.prep_time && <span className="rk-time-chip">Prep: {escapeHtml(recipe.prep_time)}</span>}
                                    {recipe.cook_time && <span className="rk-time-chip">Cook: {escapeHtml(recipe.cook_time)}</span>}
                                  </div>
                                </div>
                                <div className="rk-recipe-info">
                                  <h4 className="rk-recipe-title">{escapeHtml(recipe.title)}</h4>
                                  {this.state.selectedBookId === '__all_books__' && recipe.recipe_book && (
                                    <span className="rk-recipe-book-name" style={{ fontSize: 12, opacity: 0.7 }}>{escapeHtml(recipe.recipe_book.name)}</span>
                                  )}
                                  {recipe.categories && recipe.categories.length > 0 && (
                                    <div className="rk-recipe-cats">
                                      {recipe.categories.slice(0, 3).map((cat) => (
                                        <span key={cat.id} className="rk-cat-tag" style={{ backgroundColor: (cat.color || '#4caf50') + 'dd' }}>
                                          {escapeHtml(cat.name)}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))
                          )}
                        </div>

                        {/* Bottom Pagination */}
                        {this.renderPagination()}
                      </div>
                    )}

                    {/* SEARCH VIEW */}
                    {this.state.currentView === 'search' && (
                      <div className="rk-search-view">
                        <div className="rk-view-header">
                          <Button size="small" onClick={this.navigateHome} startIcon={<FontAwesomeIcon icon={faArrowLeft} />}>Back</Button>
                          <h2 className="rk-view-title">Search: "{this.state.searchQuery}"</h2>
                        </div>
                        <div className="rk-recipe-list">
                          {this.state.isLoading && this.state.recipes.length === 0 ? (
                            <div className="loading-spinner" style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><CircularProgress /></div>
                          ) : this.state.recipes.length === 0 ? (
                            <p className="rk-empty-state">No recipes found matching "{this.state.searchQuery}".</p>
                          ) : (
                            this.state.recipes.map((recipe) => (
                              <div key={recipe.id} className="rk-recipe-item" onClick={() => this.openRecipeDetail(recipe)}>
                                <div className="rk-recipe-image">
                                  {recipe.image_url ? (
                                    <div className="rk-image-blur-up">
                                      <img className="rk-image-thumb" src={this.getThumbUrl(recipe.image_url)} alt="" loading="lazy" />
                                      <img className="rk-image-full" src={this.getImageUrl(recipe.image_url)} alt={escapeHtml(recipe.title)} loading="lazy" onLoad={(e) => e.target.classList.add('loaded')} />
                                    </div>
                                  ) : (
                                    <div className="rk-recipe-placeholder"><FontAwesomeIcon icon={faUtensils} /></div>
                                  )}
                                  <div className="rk-image-chips">
                                    {this.renderDifficultyBadge(recipe.difficulty)}
                                    {recipe.prep_time && <span className="rk-time-chip">Prep: {escapeHtml(recipe.prep_time)}</span>}
                                    {recipe.cook_time && <span className="rk-time-chip">Cook: {escapeHtml(recipe.cook_time)}</span>}
                                  </div>
                                </div>
                                <div className="rk-recipe-info">
                                  <h4 className="rk-recipe-title">{escapeHtml(recipe.title)}</h4>
                                  {this.state.selectedBookId === '__all_books__' && recipe.recipe_book && (
                                    <span className="rk-recipe-book-name" style={{ fontSize: 12, opacity: 0.7 }}>{escapeHtml(recipe.recipe_book.name)}</span>
                                  )}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="empty-book-state">
                  <FontAwesomeIcon icon={faBook} size="4x" style={{ opacity: 0.3 }} />
                  <p>Select a recipe book or create a new one.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ===== MODALS ===== */}

        {/* DELETE CONFIRMATION */}
        <Dialog open={this.state.showDeleteModal} onClose={() => this.setState({ showDeleteModal: false })}>
          <DialogTitle>
            {this.state.recipeToDelete ? 'Delete Recipe' : 'Delete Recipe Book'}
          </DialogTitle>
          <DialogContent>
            {this.state.recipeToDelete ? (
              <p>Are you sure you want to delete "{this.state.recipeToDelete?.name}"?</p>
            ) : (
              <>
                <p>Are you sure you want to delete "{this.state.bookToDelete?.name}"?</p>
                <p>This will permanently delete all recipes in this book.</p>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ showDeleteModal: false })}>Cancel</Button>
            <Button onClick={this.state.recipeToDelete ? () => this.handleDeleteRecipe(this.state.recipeToDelete.id) : this.handleDeleteBook} color="error" variant="contained">Delete</Button>
          </DialogActions>
        </Dialog>

        {/* SHARE MODAL */}
        <Dialog open={this.state.showShareModal} onClose={() => this.setState({ showShareModal: false })}>
          <DialogTitle>Share Recipe Book</DialogTitle>
          <DialogContent>
            <TextField
              select
              label="Select user"
              value={this.state.selectedShareUserId}
              onChange={(e) => this.setState({ selectedShareUserId: e.target.value })}
              fullWidth
              sx={{ mt: 1 }}
            >
              {shareUsers.map((user) => (
                <MenuItem key={user.id} value={user.id}>{escapeHtml(user.name || user.uid)}</MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ showShareModal: false })}>Cancel</Button>
            <Button onClick={this.handleShareBook} variant="contained">Share</Button>
          </DialogActions>
        </Dialog>

        {/* SHARE LINK MODAL - Public link for a single recipe */}
        <Dialog
          open={this.state.showShareLinkModal}
          onClose={() => this.setState({ showShareLinkModal: false })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>Public Share Link</span>
              <IconButton onClick={() => this.setState({ showShareLinkModal: false })} size="small">
                <FontAwesomeIcon icon={faTimes} />
              </IconButton>
            </div>
          </DialogTitle>
          <DialogContent dividers>
            <p style={{ fontSize: 13, opacity: 0.7, marginBottom: 16 }}>
              Anyone with this link can view this recipe. The link can be revoked at any time.
            </p>
            {this.state.shareLinkLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
                <CircularProgress />
              </div>
            ) : this.state.shareLinkUrl ? (
              <>
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                  <TextField
                    fullWidth
                    value={this.state.shareLinkUrl}
                    InputProps={{ readOnly: true }}
                    size="small"
                    sx={{ backgroundColor: '#1e1e1e', borderRadius: 4 }}
                  />
                  <Button
                    variant="contained"
                    onClick={this.copyShareLink}
                    startIcon={<FontAwesomeIcon icon={faCopy} />}
                    disabled={this.state.shareLinkLoading}
                  >
                    Copy
                  </Button>
                </div>
                <Button
                  color="error"
                  onClick={() => this.revokeShareLink(this.state.viewingRecipe?.id)}
                  startIcon={<FontAwesomeIcon icon={faTrash} />}
                >
                  Revoke Link
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                fullWidth
                onClick={() => this.openShareLink(this.state.viewingRecipe)}
                startIcon={<FontAwesomeIcon icon={faLink} />}
              >
                Generate Share Link
              </Button>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ showShareLinkModal: false })}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* ADD/EDIT RECIPE MODAL */}
        <Dialog
          open={this.state.showAddRecipeModal}
          onClose={() => this.setState({ showAddRecipeModal: false, editingRecipeId: null })}
          maxWidth="sm"
          fullWidth
          fullScreen={typeof window !== 'undefined' && window.innerWidth < 600}
        >
          <DialogTitle sx={{ paddingBottom: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>{this.state.editingRecipeId ? 'Edit Recipe' : 'Add Recipe'}</span>
              <IconButton onClick={() => this.setState({ showAddRecipeModal: false, editingRecipeId: null })} size="small">
                <FontAwesomeIcon icon={faTimes} />
              </IconButton>
            </div>
          </DialogTitle>
          <DialogContent dividers sx={{ overflowY: 'auto', maxHeight: 'calc(100vh - 120px)' }}>
            {/* Title - always visible */}
            <TextField
              fullWidth
              label="Recipe Title *"
              value={this.state.recipeTitle}
              onChange={(e) => this.setState({ recipeTitle: e.target.value })}
              sx={{ mb: 2 }}
              autoFocus
            />

            {/* Details section - collapsible */}
            {(() => {
              const detailsOpen = this.state._addRecipeDetailsOpen ?? true;
              return (
                <div style={{ marginBottom: 16 }}>
                  <button
                    onClick={() => this.setState({ _addRecipeDetailsOpen: !detailsOpen })}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 0', border: 'none', backgroundColor: 'transparent',
                      color: '#ccc', fontSize: 15, fontWeight: 600, cursor: 'pointer', width: '100%'
                    }}
                  >
                    <span>Details</span>
                    <span style={{ transform: detailsOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
                  </button>
                  {detailsOpen && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, paddingLeft: 4 }}>
                      <TextField
                        label="Servings"
                        type="number"
                        value={this.state.recipeServings}
                        onChange={(e) => this.setState({ recipeServings: e.target.value })}
                      />
                      <TextField
                        select
                        label="Difficulty"
                        value={this.state.recipeDifficulty}
                        onChange={(e) => this.setState({ recipeDifficulty: e.target.value })}
                        fullWidth
                      >
                        {DIFFICULTIES.map((d) => (
                          <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        label="Prep Time"
                        value={this.state.recipePrepTime}
                        onChange={(e) => this.setState({ recipePrepTime: e.target.value })}
                        placeholder="e.g., 15 min"
                      />
                      <TextField
                        label="Cook Time"
                        value={this.state.recipeCookTime}
                        onChange={(e) => this.setState({ recipeCookTime: e.target.value })}
                        placeholder="e.g., 30 min"
                      />
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Categories - collapsible */}
            {(() => {
              const catsOpen = this.state._addRecipeCatsOpen ?? false;
              return (
                <div style={{ marginBottom: 16 }}>
                  <button
                    onClick={() => this.setState({ _addRecipeCatsOpen: !catsOpen })}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 0', border: 'none', backgroundColor: 'transparent',
                      color: '#ccc', fontSize: 15, fontWeight: 600, cursor: 'pointer', width: '100%'
                    }}
                  >
                    <span>
                      {this.state.recipeCategories.length > 0
                        ? `${this.state.recipeCategories.length} categor${this.state.recipeCategories.length === 1 ? 'y' : 'ies'}`
                        : 'Categories'}
                    </span>
                    <span style={{ transform: catsOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
                  </button>
                  {catsOpen && (
                    <div style={{
                      padding: 12, borderRadius: 8, border: '1px solid #444',
                      backgroundColor: '#1e1e1e', maxHeight: 250, overflowY: 'auto',
                    }}>
                      {this.state.categories.map((cat) => {
                        const isSelected = this.state.recipeCategories.includes(cat.id);
                        return (
                          <div
                            key={cat.id}
                            onClick={() => this.toggleCategorySelection(cat.id)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 10,
                              padding: '10px 8px', borderRadius: 6, cursor: 'pointer',
                              backgroundColor: isSelected ? (cat.color + '22') : 'transparent',
                              borderLeft: isSelected ? `3px solid ${cat.color}` : '3px solid transparent',
                              marginBottom: 2,
                            }}
                          >
                            <span style={{ fontSize: 16, opacity: 0.7 }}>{cat.icon}</span>
                            <span style={{ flex: 1, fontSize: 14, color: '#ddd' }}>{cat.name}</span>
                            {isSelected && <span style={{ color: cat.color, fontSize: 14 }}>✓</span>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Ingredients - collapsible */}
            {(() => {
              const ingOpen = this.state._addRecipeIngOpen ?? true;
              return (
                <div style={{ marginBottom: 16 }}>
                  <button
                    onClick={() => this.setState({ _addRecipeIngOpen: !ingOpen })}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 0', border: 'none', backgroundColor: 'transparent',
                      color: '#ccc', fontSize: 15, fontWeight: 600, cursor: 'pointer', width: '100%'
                    }}
                  >
                    <span>Ingredients ({this.state.recipeIngredients.length})</span>
                    <span style={{ transform: ingOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
                  </button>
                  {ingOpen && (
                    <div>
                      {this.state.recipeIngredients.map((ing, idx) => (
                        <div key={idx} style={{ marginBottom: 12, padding: 12, borderRadius: 8, backgroundColor: '#252525', border: '1px solid #333' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                            <span style={{ fontSize: 12, color: '#888' }}>Ingredient {idx + 1}</span>
                            <button
                              onClick={() => this.removeIngredientRow(idx)}
                              disabled={this.state.recipeIngredients.length <= 1}
                              style={{
                                background: 'none', border: 'none', color: '#f44336',
                                cursor: this.state.recipeIngredients.length <= 1 ? 'default' : 'pointer',
                                fontSize: 18, opacity: this.state.recipeIngredients.length <= 1 ? 0.3 : 1
                              }}
                            >
                              ✕
                            </button>
                          </div>
                          <TextField
                            fullWidth
                            placeholder="Name (e.g., flour)"
                            value={ing.name}
                            onChange={(e) => this.updateIngredient(idx, 'name', e.target.value)}
                            size="small"
                            sx={{ mb: 1 }}
                          />
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            <TextField
                              placeholder="Qty (e.g., 2)"
                              value={ing.quantity}
                              onChange={(e) => this.updateIngredient(idx, 'quantity', e.target.value)}
                              size="small"
                            />
                            <TextField
                              select
                              label="Unit"
                              value={ing.unit}
                              onChange={(e) => this.updateIngredient(idx, 'unit', e.target.value)}
                              size="small"
                              fullWidth
                              SelectProps={{ native: true }}
                            >
                              <option value="">None</option>
                              <option value="cups">Cups</option>
                              <option value="tbsp">Tbsp</option>
                              <option value="tsp">Tsp</option>
                              <option value="oz">Oz</option>
                              <option value="lb">Lb</option>
                              <option value="g">Grams</option>
                              <option value="kg">Kg</option>
                              <option value="ml">ml</option>
                              <option value="l">L</option>
                              <option value="fl oz">Fl Oz</option>
                              <option value="pint">Pint</option>
                              <option value="quart">Quart</option>
                              <option value="gal">Gallon</option>
                              <option value="pinch">Pinch</option>
                              <option value="dash">Dash</option>
                              <option value="cup">Cup</option>
                              <option value="tablespoon">Tablespoon</option>
                              <option value="teaspoon">Teaspoon</option>
                              <option value="ounce">Ounce</option>
                              <option value="pound">Pound</option>
                              <option value="gram">Gram</option>
                              <option value="kilogram">Kilogram</option>
                              <option value="milliliter">Milliliter</option>
                              <option value="liter">Liter</option>
                              <option value="fluid ounce">Fluid Ounce</option>
                              <option value="whole">Whole</option>
                              <option value="slice">Slice</option>
                              <option value="clove">Clove</option>
                              <option value="piece">Piece</option>
                              <option value="can">Can</option>
                              <option value="bunch">Bunch</option>
                              <option value="sprig">Sprig</option>
                              <option value="to taste">To Taste</option>
                              <option value="__custom__">Other...</option>
                            </TextField>
                            {ing.unit === '__custom__' && (
                              <TextField
                                placeholder="Custom unit"
                                value={ing._customUnit || ''}
                                onChange={(e) => this.updateIngredient(idx, '_customUnit', e.target.value)}
                                size="small"
                                fullWidth
                                sx={{ mt: 1 }}
                              />
                            )}
                          </div>
                        </div>
                      ))}
                      <button
                        onClick={this.addIngredientRow}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                          width: '100%', padding: '12px', borderRadius: 8,
                          border: '1px dashed #555', backgroundColor: 'transparent',
                          color: '#4caf50', fontSize: 14, fontWeight: 600, cursor: 'pointer'
                        }}
                      >
                        <FontAwesomeIcon icon={faPlus} /> Add Ingredient
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Directions - collapsible */}
            {(() => {
              const dirOpen = this.state._addRecipeDirOpen ?? true;
              return (
                <div style={{ marginBottom: 16 }}>
                  <button
                    onClick={() => this.setState({ _addRecipeDirOpen: !dirOpen })}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 0', border: 'none', backgroundColor: 'transparent',
                      color: '#ccc', fontSize: 15, fontWeight: 600, cursor: 'pointer', width: '100%'
                    }}
                  >
                    <span>Directions</span>
                    <span style={{ transform: dirOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
                  </button>
                  {dirOpen && (
                    <textarea
                      value={this.state.recipeDirection}
                      onChange={(e) => this.setState({ recipeDirection: e.target.value })}
                      placeholder="Enter directions, one step per line..."
                      rows={6}
                      style={{
                        width: '100%', minHeight: 120, padding: 12, borderRadius: 8,
                        border: '1px solid #555', backgroundColor: '#2d2d2d', color: '#fff',
                        fontFamily: 'inherit', fontSize: 14, resize: 'vertical',
                      }}
                    />
                  )}
                </div>
              );
            })()}

            {/* Notes - collapsible */}
            {(() => {
              const notesOpen = this.state._addRecipeNotesOpen ?? false;
              return (
                <div style={{ marginBottom: 16 }}>
                  <button
                    onClick={() => this.setState({ _addRecipeNotesOpen: !notesOpen })}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 0', border: 'none', backgroundColor: 'transparent',
                      color: '#ccc', fontSize: 15, fontWeight: 600, cursor: 'pointer', width: '100%'
                    }}
                  >
                    <span>Notes</span>
                    <span style={{ transform: notesOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
                  </button>
                  {notesOpen && (
                    <TextField
                      fullWidth
                      label="Notes (optional)"
                      value={this.state.recipeNotes}
                      onChange={(e) => this.setState({ recipeNotes: e.target.value })}
                      multiline
                      rows={3}
                    />
                  )}
                </div>
              );
            })()}
          </DialogContent>
          <DialogActions sx={{ padding: '8px 16px' }}>
            <Button onClick={() => this.setState({ showAddRecipeModal: false, editingRecipeId: null })}>
              Cancel
            </Button>
            <Button
              onClick={this.state.editingRecipeId ? this.handleUpdateRecipe : this.handleAddRecipe}
              variant="contained"
              disabled={!this.state.recipeTitle.trim()}
            >
              {this.state.editingRecipeId ? 'Save Changes' : 'Add Recipe'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* IMPORT FROM URL MODAL */}
        <Dialog
          open={this.state.showImportUrlModal}
          onClose={() => this.setState({ showImportUrlModal: false })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Import Recipe from Website</DialogTitle>
          <DialogContent dividers>
            <p style={{ fontSize: 13, opacity: 0.7, marginBottom: 16 }}>
              Paste a recipe URL. We'll fetch and preview the recipe so you can verify before saving.
            </p>
            <TextField
              fullWidth
              label="Website URL"
              value={this.state.importUrl}
              onChange={(e) => this.setState({ importUrl: e.target.value })}
              placeholder="https://www.example.com/recipe/chocolate-cake"
              sx={{ mb: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ showImportUrlModal: false })}>Cancel</Button>
            <Button onClick={this.previewRecipeFromUrl} variant="contained" startIcon={<FontAwesomeIcon icon={faGlobe} />}>
              Preview Recipe
            </Button>
          </DialogActions>
        </Dialog>

        {/* IMPORT PREVIEW MODAL */}
        {this.state.importedPreview && (
          <Dialog
            open={!!this.state.importedPreview}
            onClose={() => this.setState({ importedPreview: null })}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Preview Imported Recipe</DialogTitle>
            <DialogContent dividers>
              <h3 style={{ margin: '0 0 12px', color: '#fff' }}>{escapeHtml(this.state.importedPreview.title)}</h3>

              {/* Description */}
              {this.state.importedPreview.description && (
                <p style={{ fontSize: 13, opacity: 0.8, marginBottom: 16, lineHeight: 1.6 }}>{escapeHtml(this.state.importedPreview.description)}</p>
              )}

              {/* Image preview */}
              {this.state.importedPreview.image_url && (
                <div style={{ marginBottom: 16 }}>
                  <img src={this.state.importedPreview.image_url} alt="Recipe" style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, objectFit: 'cover' }} />
                </div>
              )}

              {/* Meta info */}
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16, fontSize: 13, opacity: 0.8 }}>
                {this.state.importedPreview.servings && <span>🍽 {this.state.importedPreview.servings} servings</span>}
                {this.state.importedPreview.prep_time && <span>⏱ Prep: {escapeHtml(this.state.importedPreview.prep_time)}</span>}
                {this.state.importedPreview.cook_time && <span>🔥 Cook: {escapeHtml(this.state.importedPreview.cook_time)}</span>}
              </div>

              {/* Ingredients */}
              {this.state.importedPreview.ingredients && this.state.importedPreview.ingredients.length > 0 && (
                <>
                  <h4 style={{ margin: '16px 0 8px', color: '#fff' }}>Ingredients</h4>
                  <ul style={{ paddingLeft: 20, lineHeight: 1.8 }}>
                    {this.state.importedPreview.ingredients.map((ing, idx) => (
                      <li key={idx} style={{ color: '#ddd' }}>
                        {ing.quantity && <strong>{escapeHtml(ing.quantity)} </strong>}
                        {ing.unit && <em>{escapeHtml(ing.unit)} </em>}
                        {escapeHtml(ing.name)}
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {/* Directions */}
              {this.state.importedPreview.directions && (
                <>
                  <h4 style={{ margin: '16px 0 8px', color: '#fff' }}>Directions</h4>
                  <div style={{ whiteSpace: 'pre-wrap', color: '#ddd', lineHeight: 1.6 }}>
                    {escapeHtml(this.state.importedPreview.directions)}
                  </div>
                </>
              )}

              {this.state.importedPreview.source_url && (
                <a href={escapeHtml(this.state.importedPreview.source_url)} target="_blank" rel="noopener noreferrer" className="recipe-source-link" style={{ marginTop: 16, display: 'inline-flex' }}>
                  <FontAwesomeIcon icon={faExternalLinkAlt} /> Source
                </a>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => this.setState({ importedPreview: null })}>Cancel</Button>
              <Button onClick={this.confirmImportUrl} variant="contained" startIcon={<FontAwesomeIcon icon={faPlus} />}>
                Save Recipe
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* IMPORT FROM TEXT MODAL */}
        <Dialog
          open={this.state.showImportTextModal}
          onClose={() => this.setState({ showImportTextModal: false })}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Paste Recipe Text</DialogTitle>
          <DialogContent dividers>
            <p style={{ fontSize: 13, opacity: 0.7, marginBottom: 16 }}>
              Paste recipe text copied from anywhere. The app will try to auto-detect ingredients and directions.
            </p>
            <textarea
              value={this.state.importText}
              onChange={(e) => this.setState({ importText: e.target.value })}
              placeholder={`Chocolate Cake

Ingredients:
- 2 cups flour
- 1 cup sugar
- 3 eggs
- 1 cup milk

Directions:
1. Preheat oven to 350°F
2. Mix dry ingredients
3. Add wet ingredients
4. Bake for 30 minutes`}
              rows={12}
              style={{
                width: '100%',
                minHeight: 200,
                padding: 12,
                borderRadius: 8,
                border: '1px solid #555',
                backgroundColor: '#2d2d2d',
                color: '#fff',
                fontFamily: 'inherit',
                fontSize: 14,
                resize: 'vertical',
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ showImportTextModal: false })}>Cancel</Button>
            <Button onClick={this.handleImportText} variant="contained" startIcon={<FontAwesomeIcon icon={faPaste} />}>
              Import
            </Button>
          </DialogActions>
        </Dialog>

        {/* IMPORT HTML MODAL */}
        <Dialog
          open={this.state.showImportHtmlModal}
          onClose={() => this.setState({ showImportHtmlModal: false })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Import Recipes from RecipeKeeper Export</DialogTitle>
            <p style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
              Zip up the RecipeKeeper export folder (including the images/) and upload it here.
            </p>
          <DialogContent dividers>
            <TextField
              select
              fullWidth
              label="Recipe Book"
              value={this.state.selectedBookId || ''}
              onChange={(e) => this.setState({ selectedBookId: e.target.value })}
              sx={{ mb: 2 }}
            >
              {this.state.books.map((book) => (
                <MenuItem key={book.id} value={book.id}>{book.name}</MenuItem>
              ))}
            </TextField>
            <input
              type="file"
              accept=".html,.htm,.zip"
              onChange={(e) => this.setState({ htmlFile: e.target.files[0] })}
              style={{ width: '100%', marginTop: 8 }}
            />
            {this.state.importHtmlLoading && (
              <CircularProgress sx={{ mt: 2 }} />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ showImportHtmlModal: false })}>Cancel</Button>
            <Button
              onClick={this.handleImportHtml}
              variant="contained"
              startIcon={<FontAwesomeIcon icon={faCopy} />}
              disabled={!this.state.htmlFile || !this.state.selectedBookId || this.state.importHtmlLoading}
            >
              Import All
            </Button>
          </DialogActions>
        </Dialog>

                  {/* Metric context menu for ingredients */}
                  {this.state.metricContextMenu && (() => {
                    const mode = this.getMetricMode(this.state.metricContextMenu.idx);
                    return (
                      <div
                        ref={el => { this._metricMenuEl = el; }}
                        className="rk-metric-context-menu"
                        style={{ position: 'fixed', left: this.state.metricContextMenu.x, top: this.state.metricContextMenu.y, zIndex: 9999 }}
                      >
                        <div className={`rk-metric-menu-item${mode === 'auto' ? ' rk-metric-menu-active' : ''}`} onClick={() => { this.setMetricOverride(this.state.metricContextMenu.idx, 'auto'); this.hideMetricContextMenu(); }}>
                          <FontAwesomeIcon icon={faUtensils} style={{ marginRight: 8 }} /> Auto
                        </div>
                        <div className={`rk-metric-menu-item${mode === 'grams' ? ' rk-metric-menu-active' : ''}`} onClick={() => { this.setMetricOverride(this.state.metricContextMenu.idx, 'grams'); this.hideMetricContextMenu(); }}>
                          <FontAwesomeIcon icon={faUtensils} style={{ marginRight: 8 }} /> Grams
                        </div>
                        <div className={`rk-metric-menu-item${mode === 'ml' ? ' rk-metric-menu-active' : ''}`} onClick={() => { this.setMetricOverride(this.state.metricContextMenu.idx, 'ml'); this.hideMetricContextMenu(); }}>
                          <FontAwesomeIcon icon={faUtensils} style={{ marginRight: 8 }} /> Milliliters
                        </div>
                        <div className={`rk-metric-menu-item${mode === 'imperial' ? ' rk-metric-menu-active' : ''}`} onClick={() => { this.setMetricOverride(this.state.metricContextMenu.idx, 'imperial'); this.hideMetricContextMenu(); }}>
                          <FontAwesomeIcon icon={faUtensils} style={{ marginRight: 8 }} /> Imperial
                        </div>
                        <div className="rk-metric-menu-item rk-metric-menu-copy" onClick={() => this.copyIngredient(this.state.metricContextMenu.idx)}>
                          <FontAwesomeIcon icon={faCopy} style={{ marginRight: 8 }} /> Copy
                        </div>
                      </div>
                    );
                  })()}

      </div>
    );
  }

  componentDidUpdate(prevProps, prevState) {
    this._attachScrollListener();
    // Sync state with App-level context so Header can display it
    if (this.props.onStateChange && (
      prevState.books !== this.state.books ||
      prevState.selectedBookId !== this.state.selectedBookId ||
      prevState.isLoading !== this.state.isLoading ||
      prevState.currentView !== this.state.currentView ||
      prevState.viewingRecipe !== this.state.viewingRecipe
    )) {
      const selectedBook = this.state.books.find((b) => String(b.id) === String(this.state.selectedBookId)) || null;
      // When viewing a recipe, find its actual book for ownership check
      const recipeBook = this.state.viewingRecipe ? this.state.books.find((b) => String(b.id) === String(this.state.viewingRecipe.recipe_book_id)) : null;
      this.props.onStateChange({
        books: this.state.books,
        selectedBookId: this.state.selectedBookId,
        selectedBook,
        isLoading: this.state.isLoading,
        currentView: this.state.currentView,
        viewingRecipe: this.state.viewingRecipe,
        isOwner: recipeBook ? recipeBook.is_owner : (selectedBook ? selectedBook.is_owner : false),
        canEditRecipe: (recipeBook ? (recipeBook.is_owner || recipeBook.allow_shared_edit) : (selectedBook ? (selectedBook.is_owner || selectedBook.allow_shared_edit) : false)),
        showAddRecipeModal: this.state.showAddRecipeModal,
        recipeBookId: this.state.viewingRecipe ? this.state.viewingRecipe.recipe_book_id : null,
      });
    }
  }
}

export default RecipeBook;
